/**
 * Domain hooks — TanStack Query wrappers for all main endpoints.
 */
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiFetch, uploadFile } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import type {
  SubmitLeaveRequest,
  SubmitOTRequest,
  SubmitCheckinRequest,
  ApprovalActionRequest,
  AuthProfile,
  OnboardingItem,
  OnboardingCategory,
  OnboardingDueState,
} from '@payroll/shared';

// === Multi-tenant membership (2026-05-23) ===
export interface TenantMembership {
  user_id: string;
  tenant_id: string;
  tenant_code: string;
  tenant_slug: string;
  tenant_name: string;
  tenant_status: string;
  role: 'OWNER' | 'HR' | 'MANAGER' | 'SUPERVISOR' | 'EMPLOYEE';
  emp_code: string | null;
  is_current: boolean;
}
export function useMyTenants() {
  return useQuery({
    queryKey: ['me', 'tenants'],
    queryFn: () => apiFetch<{ memberships: TenantMembership[] }>('/me/tenants').then((d) => d.memberships),
    staleTime: 5 * 60_000,
  });
}
// === Leave Packages (HR) ===
export interface LeavePackage {
  id: string;
  name: string;
  description: string | null;
  sick_quota: number;
  personal_quota: number;
  vacation_quota: number;
  prorate_on_join: boolean;
  carry_over_max: number;
  vacation_first_year_eligible: boolean;
  updated_at: string;
  member_count: number;
}
export function useLeavePackages() {
  return useQuery({
    queryKey: ['hr', 'leave-packages'],
    queryFn: () => apiFetch<{ packages: LeavePackage[] }>('/hr/leave-packages').then((d) => d.packages),
    staleTime: 60_000,
  });
}
export interface LeavePackageMember {
  emp_code: string;
  name: string;
  department: string | null;
  vacation_quota: number;
  prorate_factor: number;
  carried_days: number;
}
export function usePackageMembers(packageId: string | null, year: number) {
  return useQuery({
    queryKey: ['hr', 'leave-packages', packageId, 'members', year],
    enabled: !!packageId,
    queryFn: () => apiFetch<{ members: LeavePackageMember[]; year: number }>(`/hr/leave-packages/${packageId}/members?year=${year}`),
  });
}
export function useUpsertLeavePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<LeavePackage> & { name: string }) =>
      apiFetch<LeavePackage>('/hr/leave-packages', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'leave-packages'] }),
  });
}
export function useDeleteLeavePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean }>(`/hr/leave-packages/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'leave-packages'] });
      qc.invalidateQueries({ queryKey: ['hr', 'quotas'] });
    },
  });
}
export function useAssignLeavePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { package_id: string; year: number; emp_codes: string[]; apply_prorate: boolean }) =>
      apiFetch<{ assigned: number; prorated: boolean; year: number }>('/hr/leave-packages/assign', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'quotas'] });
      qc.invalidateQueries({ queryKey: ['hr', 'leave-packages'] });
    },
  });
}
export function useRolloverLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (from_year: number) =>
      apiFetch<{ rolled: number; from_year: number; to_year: number }>('/hr/leave-quotas/rollover', { method: 'POST', body: { from_year } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'quotas'] }),
  });
}

export function useSwitchTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenant_id: string) =>
      apiFetch<{ jwt: string; profile: AuthProfile }>('/me/switch-tenant', {
        method: 'POST',
        body: { tenant_id },
      }),
    onSuccess: (data) => {
      // Re-bind the auth store to the new tenant + nuke every cached query so
      // the next render fetches data scoped to the new tenant_id.
      useAuthStore.getState().setAuth(data.jwt, data.profile);
      qc.invalidateQueries();
    },
  });
}

// === Close readiness (เรื่อง 10) — what must be cleared before locking ===
export interface ReadinessItem {
  key: string; label: string; severity: 'red' | 'yellow'; affectsCycle: boolean; hardBlock?: boolean; count: number; detail: Record<string, unknown>;
}
export interface CloseReadiness {
  period: string; ready: boolean; affectsCycleCount: number; hardBlockCount: number; totalCount: number; items: ReadinessItem[];
  dueNow: boolean; cutoffDay: number; mode: 'salary_only' | 'attendance';
}
export function useCloseReadiness(period: string) {
  return useQuery({
    queryKey: ['close-readiness', period],
    queryFn: () => apiFetch<CloseReadiness>(`/owner/close-readiness?period=${period}`),
    staleTime: 60_000,
  });
}

// === Variance Bridge (A13) — Owner payroll analysis ===
export interface BridgeDriver { key: string; label: string; amount: number }
export interface VarianceOutlier { emp_code: string; prior: number; current: number; delta: number; pct: number | null }
export interface VarianceBridge {
  period: string; priorPeriod: string;
  curNet: number; priorNet: number; deltaNet: number; deltaPct: number | null;
  curHeadcount: number; priorHeadcount: number;
  drivers: BridgeDriver[]; reconciled: boolean;
  otRatio: number; costPerHead: number; outliers: VarianceOutlier[];
}
export interface PayrollAnalysis { bridge: VarianceBridge; summary: string | null; mode: 'rule-only' | 'llm' }
export function usePayrollAnalysis(period: string) {
  return useQuery({
    queryKey: ['payroll-analysis', period],
    queryFn: () => apiFetch<PayrollAnalysis>(`/ai/payroll/analysis?period=${period}`),
    staleTime: 60_000,
  });
}

// === รายการที่ย้ายไปหักรอบจ่ายค่าคอม (คนมีค่าคอม) ===
export interface CommissionDeferredItem {
  emp_code: string;
  earnings: Array<{ label: string; amount: number }>;
  deductions: Array<{ label: string; amount: number }>;
  net_deferred: number;
}
export interface CommissionAdjustmentsResponse {
  period: string;
  items: CommissionDeferredItem[];
  totals: { earnings: number; deductions: number; net_deferred: number; count: number };
}
export function useCommissionAdjustments(period: string) {
  return useQuery({
    queryKey: ['commission-adjustments', period],
    queryFn: () => apiFetch<CommissionAdjustmentsResponse>(`/owner/payroll/commission-adjustments?period=${period}`),
    enabled: !!period,
    staleTime: 60_000,
  });
}

// === Quota ===
export function useQuota() {
  return useQuery({
    queryKey: ['quota'],
    queryFn: () => apiFetch('/quota'),
  });
}

// === Daily briefing (AI layer A6) — open findings scoped to caller's role ===
export interface BriefingFinding {
  id: string;
  rule_code: string;
  severity: 'INFO' | 'WARN' | 'ERROR';
  title: string;
  suggested_action: { kind?: string; [k: string]: unknown } | null;
  period: string | null;
}
export interface BriefingData {
  mode: 'rule-only' | 'llm';
  summary: string | null;
  findings: BriefingFinding[];
}
export function useBriefing() {
  return useQuery({
    queryKey: ['briefing'],
    queryFn: () => apiFetch<BriefingData>('/ai/briefing'),
    staleTime: 60_000,
  });
}
export function useUpdateFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: 'done' | 'dismissed'; reason?: string }) =>
      apiFetch(`/ai/findings/${v.id}`, { method: 'POST', body: { status: v.status, reason: v.reason } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['briefing'] }),
  });
}

// === BYOK settings (AI layer A14) — OWNER only ===
export interface AISettings {
  enabled: boolean;
  provider: string | null;
  model: string | null;
  monthly_token_cap: number;
  tokens_used_this_month: number;
  last_error: string | null;
  key_set: boolean;
  key_last4: string | null;
  seed_tokens_remaining: number;
  on_seed_trial: boolean;
  ask_daily_limit: number;
}
export function useAISettings() {
  return useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => apiFetch<AISettings>('/ai/settings'),
  });
}
export function useUpdateAISettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<{ enabled: boolean; provider: string; model: string; monthly_token_cap: number; api_key: string; ask_daily_limit: number }>) =>
      apiFetch<AISettings>('/ai/settings', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-settings'] }),
  });
}
export function useTestAIKey() {
  return useMutation({
    mutationFn: () => apiFetch<{ ok: boolean; message: string }>('/ai/settings/test', { method: 'POST' }),
  });
}

// === Ask AI (A8) — grounded Q&A over read-tools scoped to the caller's role ===
export interface AskAIResult {
  available: boolean;
  answer: string | null;
  message?: string;
  tools_used?: string[];
  rate_limited?: boolean;
  quota?: { limit: number; used: number; remaining: number };
}
export function useAskAI() {
  return useMutation({
    mutationFn: (question: string) => apiFetch<AskAIResult>('/ai/ask', { method: 'POST', body: { question } }),
  });
}

// === Setup checklist (AI layer A17) — deterministic, no key needed ===
export interface SetupItem { key: string; label: string; done: boolean; route: string; optional?: boolean; manual?: boolean }
export interface SetupChecklist { items: SetupItem[]; pct: number; complete: boolean; nextStep: SetupItem | null }
export function useAckSetupStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { key: string; done: boolean }) =>
      apiFetch<{ acked: string[] }>('/ai/setup/ack', { method: 'POST', body: args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-setup'] }),
  });
}
// === Role permission preview (A19) — derived matrix for assign-time UI ===
export interface RoleMatrixItem { key: string; label: string; allowed: boolean; why: string }
export interface RoleMatrix { role: string; known: boolean; items: RoleMatrixItem[] }
export function useRolePermissions(role: string) {
  return useQuery({
    queryKey: ['ai-permissions', role],
    queryFn: () => apiFetch<RoleMatrix>(`/ai/permissions/${role}`),
    enabled: !!role,
    staleTime: 5 * 60_000,
  });
}

// === Wayfinding LLM fallback (A18) — HumanAI-funded, menu-only ===
export function useWayfind() {
  return useMutation({
    mutationFn: (v: { query: string; candidates: { label: string; route: string }[] }) =>
      apiFetch<{ matches: { label: string; route: string }[] }>('/ai/wayfind', { method: 'POST', body: v }),
  });
}

export function useSetupChecklist() {
  return useQuery({
    queryKey: ['ai-setup'],
    queryFn: () => apiFetch<SetupChecklist>('/ai/setup'),
    staleTime: 60_000,
  });
}

// === History (leave + OT submitted by self) ===
export function useHistory() {
  return useQuery({
    queryKey: ['history'],
    queryFn: () => apiFetch('/history'),
  });
}

// === Submit leave ===
export function useSubmitLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitLeaveRequest) =>
      apiFetch('/leave', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quota'] });
      qc.invalidateQueries({ queryKey: ['history'] });
    },
  });
}

// === My Requests / My Decisions (TASK 25) ===
//
// /me/requests  = ใบที่ฉันส่ง (leave + OT + escalation)
// /me/decisions = ใบที่ฉันตัดสิน (l*_approver=me + resolved_by=me + reviewed_by=me)
//
// ทุก role เปิดได้. EMPLOYEE ที่ไม่เคย approve → /me/decisions คืน items=[] (ไม่ใช่ 403)
export interface TimelineStep {
  level: 1 | 2 | 3;
  approver_line_user_id: string | null;
  approver_name: string | null;
  decided_at: string | null;
  decision: 'pending' | 'approved' | 'rejected' | 'info_requested' | 'conditionally_approved';
}
export interface MyRequestItem {
  kind: 'leave' | 'ot' | 'escalation';
  id: string;
  record_id: string;
  request_group_id?: string;
  date: string;
  end_date?: string;
  days_count?: number;
  type: string;
  reason: string | null;
  status: string;
  current_level: number;
  required_levels: number;
  start_time?: string | null;
  end_time?: string | null;
  period?: string;
  is_emergency?: boolean;
  is_backdated?: boolean;
  submitted_at: string;
  final_decided_at: string | null;
  rejection_reason: string | null;
  resolution_note?: string | null;
  conditional_evidence_due?: string | null;
  evidence_url?: string | null;
  cancel_requested?: boolean;   // owner/HR ขอยกเลิกใบที่อนุมัติแล้ว รอเรายินยอม
  cancel_reason?: string | null;
  approval_timeline: TimelineStep[];
}
export interface MyDecisionItem {
  kind: 'leave' | 'ot' | 'emergency_review' | 'escalation';
  id: string;
  record_id: string;
  target_emp_code: string;
  target_emp_name: string;
  target_department: string | null;
  date: string;
  end_date?: string | null;
  type: string;
  reason: string | null;
  start_time?: string | null;
  end_time?: string | null;
  my_role_in_decision: 'L1' | 'L2' | 'L3' | 'reviewer' | 'resolver';
  decision: 'approved' | 'rejected' | 'info_requested' | 'conditionally_approved' | 'resolved' | 'ignored';
  decided_at: string;
  decision_note: string | null;
  current_status: string;
  current_level: number;
  required_levels: number;
}
export interface MyRequestsResp {
  items: MyRequestItem[];
  next_cursor: string | null;
  summary: { pending_count: number; needs_action: number; total: number };
}
export interface MyDecisionsResp {
  items: MyDecisionItem[];
  next_cursor: string | null;
  summary: {
    total: number;
    approved: number;
    rejected: number;
    info_requested: number;
    resolved: number;
    ignored: number;
  };
}
export function useMyRequests(params: { status?: string; kind?: string; from?: string; to?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.kind) qs.set('kind', params.kind);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['my-requests', params],
    queryFn: () => apiFetch<MyRequestsResp>(`/me/requests${suffix}`),
    staleTime: 30_000,
  });
}
// ยกเลิกคำขอลา/OT ของตัวเอง (เฉพาะใบที่ยังรออนุมัติ)
export function useCancelRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: 'leave' | 'ot'; record_id: string }) =>
      apiFetch('/me/requests/cancel', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-requests'] });
      qc.invalidateQueries({ queryKey: ['quota'] });
      qc.invalidateQueries({ queryKey: ['history'] });
    },
  });
}

// พนักงานตอบคำขอยกเลิก (ของ owner/HR) ที่ค้างอยู่บนใบที่อนุมัติแล้ว
export function useConsentCancel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: 'leave' | 'ot'; record_id: string; consent: boolean }) =>
      apiFetch('/me/requests/consent-cancel', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-requests'] });
      qc.invalidateQueries({ queryKey: ['quota'] });
      qc.invalidateQueries({ queryKey: ['history'] });
    },
  });
}

// === Owner/HR: ใบที่อนุมัติแล้ว แต่ยังมาไม่ถึง (ขอยกเลิกได้ ต้องให้พนักงานยินยอม) ===
export interface UpcomingApprovedLeave {
  record_id: string;
  leave_id: string;
  request_group_id: string | null;
  emp_code: string;
  emp_name: string;
  date: string;
  end_date: string;
  days_count: number;
  type: string;
  type_label: string;
  reason: string | null;
  cancel_pending: boolean;
  cancel_reason: string | null;
}
export interface UpcomingApprovedOt {
  record_id: string;
  ot_id: string;
  emp_code: string;
  emp_name: string;
  date: string;
  type: string;
  type_label: string;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
  cancel_pending: boolean;
  cancel_reason: string | null;
}
export function useUpcomingApproved() {
  return useQuery({
    queryKey: ['upcoming-approved'],
    queryFn: () => apiFetch<{ leaves: UpcomingApprovedLeave[]; ots: UpcomingApprovedOt[] }>('/approvals/upcoming-approved'),
    staleTime: 30_000,
  });
}
export function useRequestCancelApproved() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: 'leave' | 'ot'; record_id: string; reason: string }) =>
      apiFetch('/approvals/request-cancel', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['upcoming-approved'] }),
  });
}
export interface MyAttendanceDay {
  date: string;
  shift_code: string | null;
  shift_start: string | null;
  shift_end: string | null;
  events: Record<string, {
    at: string;
    late_minutes: number;
    geofence_passed: boolean;
    selfie_path: string | null;
    deduction_waived: boolean;
    waiver_evidence_url: string | null; // HR เท่านั้น — /me/attendance/history คืน null เสมอ
  }>;
  leave: { type: string; reason: string | null } | null;
  holiday: string | null;
  status: 'working' | 'short_work' | 'on_leave' | 'absent' | 'holiday';
}
export interface MyAttendanceResp {
  range: { from: string; to: string };
  summary: { working_days: number; short_days: number; leave_days: number; absent_days: number; total_late_minutes: number };
  days: MyAttendanceDay[];
}
export function useMyAttendance(params: { from?: string; to?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['my-attendance', params],
    queryFn: () => apiFetch<MyAttendanceResp>(`/me/attendance/history${suffix}`),
    staleTime: 30_000,
  });
}

export interface EmployeeAttendanceResp extends MyAttendanceResp {
  employee: { emp_code: string; name: string };
}
// HR/Owner: ดูประวัติเข้างานของพนักงานคนหนึ่งทั้งช่วง (เลือกคน + ช่วงวันที่)
export function useEmployeeAttendance(empCode: string | null, params: { from?: string; to?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['employee-attendance', empCode, params],
    queryFn: () => apiFetch<EmployeeAttendanceResp>(`/hr/attendance/employee/${encodeURIComponent(empCode!)}${suffix}`),
    enabled: !!empCode,
    staleTime: 30_000,
    // ลิงก์รูปในผลลัพธ์หมดอายุใน 5 นาที และแอปปิด refetchOnWindowFocus — ต้องขอใหม่ก่อนหมดอายุ
    refetchInterval: 4 * 60_000,
  });
}

// HR/Owner/Manager: สรุปมาสายทั้งบริษัทในช่วงวันที่เลือกเอง (คำนวณสด ไม่ใช่ monthly_summary)
export interface LateReportRow {
  emp_code: string;
  name: string;
  department: string | null;
  late_days: number;
  late_minutes: number;
  worst_date: string | null;
  worst_minutes: number;
  /** ขาด/ลา ตามเกณฑ์สรุปเวลาเงินเดือน (ลาเป็นวัน รองรับ 0.5) · null = คนที่ไม่ร่วมสรุปเวลา */
  absent_days: number | null;
  leave_days: number | null;
  /** leave_days แยกตามประเภท (มีเฉพาะประเภทที่ลาจริง) · null เงื่อนไขเดียวกับ leave_days */
  leave_by_type: Record<string, number> | null;
  /** วันลาที่เป็นลาฉุกเฉิน นับอยู่ใน leave_days แล้ว · null เงื่อนไขเดียวกับ leave_days */
  emergency_days: number | null;
}
/** คอลัมน์ลาตามประเภท ชื่อไทยและลำดับมาจาก worker */
export interface LeaveColumn {
  type: string;
  label: string;
}
export interface LateReportResp {
  from: string;
  to: string;
  totals: { employees_late: number; late_days: number; late_minutes: number; absent_days: number; leave_days: number; leave_by_type: Record<string, number> };
  leave_columns: LeaveColumn[];
  rows: LateReportRow[];
}
export function useLateReport(params: { from: string; to: string }) {
  const qs = new URLSearchParams({ from: params.from, to: params.to });
  return useQuery({
    queryKey: ['late-report', params],
    queryFn: () => apiFetch<LateReportResp>(`/hr/late-report?${qs}`),
    staleTime: 30_000,
  });
}

// HR ลงเวลาย้อนหลังให้พนักงานเอง (พนักงานลืมสแกนและไม่ได้ยื่นคำขอ)
export interface ManualAttendanceEntryInput {
  emp_code: string;
  date: string;
  kind: 'morning_in' | 'lunch_out' | 'lunch_in' | 'evening_out';
  time: string;
  reason: string;
  waive_deduction?: boolean; // เฉพาะ morning_in/evening_out และต้องมี evidence_path
  evidence_path?: string;
}
export function useManualAttendanceEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ManualAttendanceEntryInput) =>
      apiFetch<{ replaced_existing: boolean; minutes_late: number; label: string; deduction_waived: boolean }>('/hr/attendance/manual-entry', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employee-attendance'] });
      qc.invalidateQueries({ queryKey: ['escalations'] }); // ลงเวลาย้อนหลัง → resolve การ์ด → หายจากรายการตรวจสอบ
    },
  });
}

// === HR Attendance Today (TASK 22b) ===
export interface AttendanceTodayEmp {
  emp_code: string;
  first_name: string;
  last_name: string;
  nickname: string | null;
  department: string | null;
}
export interface UnscannedRow extends AttendanceTodayEmp {
  shift_code: string | null;
  shift_start: string | null;
  minutes_late: number;
  last_nudged_at: string | null;
  can_nudge_now: boolean;
}
export interface ScannedRow extends AttendanceTodayEmp {
  morning_in_at: string;
  minutes_late: number;
  geofence_passed: boolean;
  approval_status: string;
  selfie_r2_path: string | null;
}
export interface OnLeaveRow extends AttendanceTodayEmp {
  leave_type: string;
  reason: string | null;
}
export interface ShortWorkRow extends AttendanceTodayEmp {
  shift_code: string | null;
  shift_end: string | null;
}
export interface AttendanceTodayResp {
  date: string;
  summary: { total_in_shift: number; scanned_morning_in: number; on_leave: number; unscanned: number; short_work: number };
  by_shift: Array<{
    shift_code: string;
    shift_label: string;
    start_time: string;
    end_time: string;
    in_shift: number;
    scanned: number;
    on_leave: number;
    unscanned: number;
    short_work: number;
  }>;
  unscanned_list: UnscannedRow[];
  scanned_list: ScannedRow[];
  on_leave_list: OnLeaveRow[];
  short_work_list: ShortWorkRow[];
}
export function useAttendanceToday(date?: string) {
  return useQuery({
    queryKey: ['hr-attendance-today', date],
    queryFn: () => apiFetch<AttendanceTodayResp>(`/hr/attendance/today${date ? `?date=${date}` : ''}`),
    refetchInterval: 60_000,
  });
}
export interface NudgeResult {
  sent: string[];
  throttled: Array<{ emp_code: string; reason: 'throttle' | 'quiet_hours' | 'no_line_id'; next_available_at?: string }>;
  failed: Array<{ emp_code: string; error: string }>;
}
export function useNudge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { emp_codes: string[]; custom_message?: string; date?: string }) =>
      apiFetch<NudgeResult>('/hr/attendance/nudge', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-attendance-today'] }),
  });
}

export function useMyDecisions(params: { kind?: string; decision?: string; from?: string; to?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.kind) qs.set('kind', params.kind);
  if (params.decision) qs.set('decision', params.decision);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['my-decisions', params],
    queryFn: () => apiFetch<MyDecisionsResp>(`/me/decisions${suffix}`),
    staleTime: 30_000,
  });
}

// === Submit OT ===
export function useSubmitOT() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitOTRequest) =>
      apiFetch('/ot', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['history'] }),
  });
}

// === Check-in (4 events / day) ===
import type { CheckinKind, CheckinDayStatus, WorkMode } from '@payroll/shared';

export function useCheckinStatus() {
  return useQuery({
    queryKey: ['checkin-status'],
    queryFn: () =>
      apiFetch<CheckinDayStatus & {
        windows: Record<CheckinKind, string>;
        kinds: readonly CheckinKind[];
        shift: { name: string; start_time: string; end_time: string } | null;
        attendance_exempt?: boolean;
        work_mode?: WorkMode;
      }>(
        '/checkin/status',
      ),
    refetchInterval: 60_000,
  });
}

export function useSubmitCheckin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      file: Blob;
      filename?: string;
      latitude: number;
      longitude: number;
      kind: CheckinKind;
      accuracy?: number;
      location_mode?: 'office' | 'wfh';
    }) => {
      const selfiePath = await uploadFile(args.file, 'selfie', args.filename);
      return apiFetch<{
        date: string;
        kind: CheckinKind;
        geofence_passed: boolean;
        approval_status: string;
        shift_name: string | null;
        shift_start: string | null;
        late_minutes: number;
        unscheduled_work?: boolean;
        warning?: string | null;
      }>('/checkin', {
        method: 'POST',
        body: {
          selfie_path: selfiePath,
          latitude: args.latitude,
          longitude: args.longitude,
          kind: args.kind,
          accuracy: args.accuracy,
          location_mode: args.location_mode,
        } satisfies SubmitCheckinRequest,
        idempotencyKey: crypto.randomUUID(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checkin-status'] }),
  });
}

// === Approvals ===
export function usePendingApprovals() {
  return useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: () => apiFetch('/approvals/pending'),
    refetchInterval: 30_000,
  });
}

export function useActOnApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApprovalActionRequest) =>
      apiFetch('/approvals/act', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

// === Slip ===
export function useSlipPeriods() {
  return useQuery({
    queryKey: ['slip-periods'],
    queryFn: () => apiFetch('/slip/periods'),
  });
}

export function useSlip(period: string | null) {
  return useQuery({
    queryKey: ['slip', period],
    queryFn: () => apiFetch(`/slip?period=${period}`),
    enabled: !!period,
  });
}

// === Geolocation ===
/**
 * Geolocation with hard timeout + graceful fallback.
 *
 * Why:
 *   - On iOS LINE LIFF the native geolocation Promise can hang forever
 *     (no resolve, no reject) — observed via the on-screen debug panel
 *     when "start" logged but "geo ok" never did. The browser's own
 *     `timeout` option isn't honoured reliably inside the LIFF webview.
 *   - Coarse position (enableHighAccuracy=false) returns in ~1s on a
 *     normal device, so coarse + a Promise.race fallback is the right
 *     escape hatch.
 *
 * Returns `{ latitude: 0, longitude: 0, available: false }` on timeout/
 * deny so the caller can still submit; the Worker treats a missing
 * fix as a failed geofence (approval_status='pending_review') instead
 * of blocking the check-in.
 */
export interface GeoResult {
  latitude: number;
  longitude: number;
  available: boolean;
  accuracy?: number;
}
/**
 * ⭐⭐ เคสจริง Mangmee 4 ส.ค. 2569: **3,813 เช็คอินจาก 86 คน (≈70% ของทั้งหมด)
 * ส่งพิกัด (0,0) มา** เพราะของเดิมขอตำแหน่งแบบ `enableHighAccuracy:false`
 * timeout แค่ 6 วิ พออ่านไม่ทัน/ไม่ได้ ก็คืน 0,0 แล้วส่งขึ้นเซิร์ฟเวอร์เหมือนพิกัดจริง
 *
 * แก้ 3 อย่าง:
 *  1) ลองแบบแม่นสูง (GPS จริง) ก่อน — ของเดิมเอาแต่ตำแหน่งจาก wifi/เสาสัญญาณ
 *  2) ถ้าไม่ทัน ค่อยถอยไปแบบเร็ว แทนที่จะยอมแพ้เลย (ได้ตำแหน่งหยาบดีกว่าไม่ได้เลย)
 *  3) ส่ง `available` ขึ้นไปด้วย เซิร์ฟเวอร์จะได้แยกออกว่า "อ่านไม่ได้" ไม่ใช่ "อยู่ที่ 0,0"
 */
function readPosition(opts: PositionOptions): Promise<GeoResult> {
  return new Promise<GeoResult>((resolve) => {
    let settled = false;
    const done = (r: GeoResult) => { if (!settled) { settled = true; resolve(r); } };
    // เผื่อบางเครื่องไม่ยิง error callback เลย — กันค้างด้วยนาฬิกาของเราเอง
    const guard = setTimeout(() => done({ latitude: 0, longitude: 0, available: false }), (opts.timeout ?? 10_000) + 1_000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(guard);
        done({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          available: true,
          accuracy: pos.coords.accuracy,
        });
      },
      () => { clearTimeout(guard); done({ latitude: 0, longitude: 0, available: false }); },
      opts,
    );
  });
}

export function useGeolocation() {
  return async (): Promise<GeoResult> => {
    if (!navigator.geolocation) {
      return { latitude: 0, longitude: 0, available: false };
    }
    // รอบ 1 — GPS จริง แม่นพอเอาไปตัดสินพื้นที่ได้ (ยอมช้าหน่อย)
    const precise = await readPosition({ enableHighAccuracy: true, timeout: 12_000, maximumAge: 30_000 });
    if (precise.available) return precise;
    // รอบ 2 — ถอยไปแบบเร็ว (wifi/เสาสัญญาณ) ดีกว่าไม่ได้พิกัดเลย
    return readPosition({ enableHighAccuracy: false, timeout: 6_000, maximumAge: 120_000 });
  };
}

// === HR: Employees ===
// includeLeft=true → รวมพนักงานที่ลาออก/ถูกเลิกจ้าง (สำหรับหน้าจัดการพนักงาน tab
// "ออกแล้ว" / ออกใบรับรอง / นำเข้าเช็คซ้ำ). ดีฟอลต์ซ่อนคนออกแล้วทุกหน้า.
export function useEmployees(enabled = true, includeLeft = false) {
  return useQuery({
    queryKey: ['hr', 'employees', includeLeft ? 'all' : 'active'],
    queryFn: () => apiFetch(`/hr/employees${includeLeft ? '?include=left' : ''}`),
    enabled,
  });
}

export function useUpsertEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/hr/employees', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'employees'] }),
  });
}

// (Re)issue a LINE pairing code for an existing employee — link later / re-link.
export function useReissuePairing() {
  return useMutation({
    mutationFn: (args: { emp_code: string; role?: string }) =>
      apiFetch<{ emp_code: string; pairing_code: string; expires_at: string }>(
        `/hr/employees/${encodeURIComponent(args.emp_code)}/pairing-code`,
        { method: 'POST', body: { role: args.role ?? 'EMPLOYEE' } },
      ),
  });
}

// สายอนุมัติทั้งบริษัท (L1=หัวหน้า, L2/L3=คนที่กำหนด)
export interface ApprovalChainConfig {
  levels: number;
  l2_emp_code: string | null;
  l3_emp_code: string | null;
}
export function useApprovalChain() {
  return useQuery({
    queryKey: ['owner', 'approval-chain'],
    queryFn: () => apiFetch<ApprovalChainConfig>('/owner/approval-chain'),
  });
}
export function useSetApprovalChain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApprovalChainConfig) =>
      apiFetch<ApprovalChainConfig>('/owner/approval-chain', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner', 'approval-chain'] }),
  });
}

// ตั้งสิทธิ์ใช้งานระบบ (role) ให้พนักงานจากหน้ารายชื่อ (OWNER เท่านั้น)
export function useSetEmployeeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; role: 'OWNER' | 'HR' | 'MANAGER' | 'SUPERVISOR' | 'EMPLOYEE' }) =>
      apiFetch<{ emp_code: string; role: string; applied: boolean }>(
        `/hr/employees/${encodeURIComponent(args.emp_code)}/role`,
        { method: 'POST', body: { role: args.role } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
      qc.invalidateQueries({ queryKey: ['owner', 'owners'] });
    },
  });
}

// === เจ้าของระบบ (OWNER) — ดูรายชื่อ + เพิ่ม/ถอด (OWNER เท่านั้น) ===
export interface OwnerListItem {
  emp_code: string;
  name: string;
  position: string | null;
  department: string | null;
  role: string | null;
  line_paired: boolean;
}
export function useOwners(enabled = true) {
  return useQuery({
    queryKey: ['owner', 'owners'],
    queryFn: () => apiFetch<{ owners: OwnerListItem[]; employees: OwnerListItem[] }>('/owner/owners'),
    enabled,
    staleTime: 30_000,
  });
}

// ทดสอบส่งแจ้งเตือนเข้า LINE ของพนักงานคนหนึ่ง (พิสูจน์ว่าเขาแอด OA แล้วรับ push ได้)
export function useTestNotify() {
  return useMutation({
    mutationFn: (empCode?: string) =>
      apiFetch<{ ok: boolean; message?: string; reason?: string; detail?: string }>('/owner/test-notify', {
        method: 'POST',
        body: empCode ? { emp_code: empCode } : {},
      }),
  });
}

// Unbind the employee's current LINE account.
export function useRevokeLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (empCode: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/revoke-line`, { method: 'POST', body: {} }),
    onSuccess: (_d, empCode) => {
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
      qc.invalidateQueries({ queryKey: ['hr', 'line-history', empCode] });
    },
  });
}

// Hard-delete a mistakenly-created employee (blocked once they have records).
export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (empCode: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'employees'] }),
  });
}

// === HR: Allowances ===
export function useAllowances() {
  return useQuery({
    queryKey: ['hr', 'allowances'],
    queryFn: () => apiFetch('/hr/allowances'),
  });
}

export function useUpsertAllowance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/hr/allowances', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'allowances'] }),
  });
}

// === HR: Holidays ===
export function useHolidays(year?: number) {
  return useQuery({
    queryKey: ['hr', 'holidays', year ?? 'current'],
    queryFn: () => apiFetch(year ? `/hr/holidays?year=${year}` : '/hr/holidays'),
  });
}

export function useRecomputeCompHolidays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) =>
      apiFetch<{ year: number; compensatory: number }>(`/hr/holidays/recompute-comp?year=${year}`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'holidays'] }),
  });
}
export function useUpsertHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/hr/holidays', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'holidays'] }),
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/holidays/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'holidays'] }),
  });
}

// === HR: Leave Quotas ===
export interface QuotaRow {
  emp_code: string;
  year: number;
  sick_quota: number;
  personal_quota: number;
  vacation_quota: number;
  package_id: string | null;
  package_name: string | null;
  prorate_factor: number;
  carried_days: number;
}
export function useQuotas(year?: number) {
  return useQuery({
    queryKey: ['hr', 'quotas', year],
    queryFn: () => apiFetch<QuotaRow[]>(`/hr/quotas${year ? `?year=${year}` : ''}`),
  });
}

export function useUpsertQuota() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/hr/quotas', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'quotas'] }),
  });
}

// === Pending Changes ===
export function usePendingChanges() {
  return useQuery({
    queryKey: ['pending-changes'],
    queryFn: () => apiFetch('/hr/pending-changes'),
    refetchInterval: 30_000,
  });
}

export function useDecidePendingChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { change_id: string; decision: 'approved' | 'rejected'; decision_note?: string }) =>
      apiFetch('/owner/pending-changes/decide', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-changes'] }),
  });
}

// === Owner: Reconcile ===
export function useReconcile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string }) =>
      apiFetch('/owner/reconcile', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['escalations'] }),
  });
}

// === Owner: Payroll ===
export function useRunPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; force?: boolean; salary_only?: boolean }) =>
      apiFetch('/owner/payroll/run', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

export function useLockPayroll() {
  const qc = useQueryClient();
  return useMutation({
    // #hr-delegation Phase 3 — HR ที่ระดับ tier=pending จะได้ { queued:true, message }
    // (คำขอถูกส่งให้ owner) แทนการปิดงวดจริง. caller เช็ค queued เพื่อแสดงข้อความ.
    mutationFn: (body: { period: string }) =>
      apiFetch<{ queued?: boolean; message?: string }>('/owner/payroll/lock', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

// === Pending actions (HR Delegation Phase 3) — คำขอ HR รอ owner อนุมัติ ===
export interface PendingAction {
  id: string;
  requested_by_name: string | null;
  action_type: string;
  params: Record<string, unknown>;
  summary: string;
  status: string;
  created_at: string;
}
export function usePendingActions() {
  return useQuery({
    queryKey: ['pending-actions'],
    queryFn: () => apiFetch<{ items: PendingAction[] }>('/owner/pending-actions').then((d) => d.items),
    staleTime: 30_000,
  });
}
export function useRejectPendingAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      apiFetch(`/owner/pending-actions/${id}/reject`, { method: 'POST', body: { note } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-actions'] }),
  });
}

// === Trial onboarding: pre-register employees (name+nid) ===
export interface RegisterResult {
  registered: number;
  skipped: Array<{ name: string; reason: string }>;
  created: Array<{ emp_code: string; first_name: string; last_name: string; has_nid: boolean }>;
  tenant_code: string | null;
}
export interface RegisterRow {
  first_name: string;
  last_name: string;
  national_id?: string;
  department?: string;
  position?: string;
}
export function useRegisterEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: RegisterRow[]) =>
      apiFetch<RegisterResult>('/owner/employees/register', { method: 'POST', body: { rows } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

// === Bulk employee master import (no salary) ===
export interface BulkImportEmployee {
  emp_code: string;
  first_name?: string;
  last_name?: string;
  nickname?: string;
  email?: string;
  phone?: string;
  department?: string;
  position?: string;
  start_date?: string;
  status: 'active' | 'probation' | 'resigned' | 'terminated';
  national_id?: string;
  bank?: string;
  bank_account_full?: string;
  address_current?: string;
  emergency_phone?: string;
}
export interface BulkImportResult {
  written: number;
  failed: number;
  errors: Array<{ emp_code: string; msg: string }>;
}
export function useBulkImportEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (employees: BulkImportEmployee[]) =>
      apiFetch<BulkImportResult>('/hr/employees/bulk-import', { method: 'POST', body: { employees } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// === Owner: read base-salary history (decrypted) — current + past segments ===
export function useEmployeeSalary(empCode: string | null) {
  return useQuery({
    queryKey: ['employee-salary', empCode],
    enabled: !!empCode,
    queryFn: () =>
      apiFetch<{
        emp_code: string;
        base_salary: number | null;
        effective_from: string | null;
        history: Array<{ effective_from: string; effective_to: string | null; base_salary: number }>;
      }>(`/owner/employees/${encodeURIComponent(empCode!)}/salary`),
  });
}

// === Owner: set base salary (relocated from the old onboard form) ===
export function useSetEmployeeSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; base_salary: number; effective_date: string }) =>
      apiFetch(`/owner/employees/${encodeURIComponent(args.emp_code)}/salary`, {
        method: 'POST',
        body: { base_salary: args.base_salary, effective_date: args.effective_date },
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee-salary', vars.emp_code] });
    },
  });
}

// === Owner: delete one salary entry (wrong/duplicate) ===
export function useDeleteEmployeeSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; effective_date: string }) =>
      apiFetch(`/owner/employees/${encodeURIComponent(args.emp_code)}/salary?effective_date=${encodeURIComponent(args.effective_date)}`, {
        method: 'DELETE',
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee-salary', vars.emp_code] });
    },
  });
}

// === Owner: bulk base-salary helper (ตั้งฐานเงินเดือนหลายคนพร้อมกัน) ===
export interface SalaryRange { min: number; mid: number; max: number }
export interface BulkSalaryEmp {
  emp_code: string;
  name: string;
  position: string | null;
  department: string | null;
  employee_type: string | null;
  suggested: number | null;
  suggested_source: 'band' | 'structure' | null;
  band: SalaryRange | null;
  matched_title: string | null;
  match_quality: 'exact' | 'fuzzy' | null;
}
export interface SalaryReference extends SalaryRange {
  kind: 'position' | 'structure';
  id: string;
  title: string;
  level: number | null;
  department: string | null;
}
export interface BulkSalaryPrep {
  today: string;
  total_active: number;
  missing_count: number;
  employees: BulkSalaryEmp[];
  references: { positions: SalaryReference[]; structure: SalaryReference[] };
}
export function useBulkSalaryPrep(enabled: boolean) {
  return useQuery({
    queryKey: ['bulk-salary-prep'],
    queryFn: () => apiFetch<BulkSalaryPrep>('/owner/salary/bulk-prep'),
    enabled,
  });
}
export function useSyncPayrollInclusion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_codes: string[] }) =>
      apiFetch<{ included: number; excluded: number; excluded_codes: string[] }>(
        '/owner/employees/payroll-inclusion-sync', { method: 'POST', body: args },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
    },
  });
}
export function useBulkSetSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { effective_date: string; items: Array<{ emp_code: string; base_salary: number }> }) =>
      apiFetch<{ ok: boolean; set: number; failed: Array<{ emp_code: string; reason: string }> }>(
        '/owner/salary/bulk', { method: 'POST', body: args },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bulk-salary-prep'] });
      qc.invalidateQueries({ queryKey: ['close-readiness'] });
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
    },
  });
}

// === HR permissions admin (HR Delegation Phase 4, OWNER-only) ===
export type CapGrant = 'allow' | 'pending' | 'deny';
export interface HrPermissionsConfig {
  /** false = ผู้ดูเป็น HR เต็มสิทธิ์ (ตั้งได้เฉพาะ HR คนอื่น ไม่แตะระดับบริษัท) */
  can_edit_default?: boolean;
  default_tier: number;
  capabilities: Array<{ key: string; label: string }>;
  tiers: Record<string, Record<string, CapGrant>>;
  members: Array<{ user_id: string; emp_code: string | null; name: string; role: string; tier: number | null }>;
}
export function useHrPermissions() {
  return useQuery({
    queryKey: ['hr-permissions'],
    queryFn: () => apiFetch<HrPermissionsConfig>('/owner/hr-permissions'),
    staleTime: 60_000,
  });
}
export function useSaveHrPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { default_tier?: number; overrides?: Array<{ user_id: string; tier: number | null }> }) =>
      apiFetch('/owner/hr-permissions', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-permissions'] });
      qc.invalidateQueries({ queryKey: ['my-capabilities'] });
    },
  });
}

// === สิทธิ์ตามบทบาท (หัวหน้างาน / ผู้จัดการ) — ต่อบทบาท, แยก ดู/แก้ ต่อโมดูล ===
export interface ModulePerm { view: boolean; edit: boolean }
export type RolePermissionMap = Record<string, ModulePerm>;
export interface TeamRolePermissionsConfig {
  modules: Array<{ key: string; label: string }>;
  scope: string;
  scope_label: string;
  roles: Array<{ role_target: 'SUPERVISOR' | 'MANAGER'; permissions: RolePermissionMap; is_default: boolean }>;
  defaults: Record<'SUPERVISOR' | 'MANAGER', RolePermissionMap>;
}
export function useTeamRolePermissions() {
  return useQuery({
    queryKey: ['team-role-permissions'],
    queryFn: () => apiFetch<TeamRolePermissionsConfig>('/owner/role-permissions'),
    staleTime: 60_000,
  });
}
export function useSaveTeamRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { role_target: 'SUPERVISOR' | 'MANAGER'; permissions: RolePermissionMap }) =>
      apiFetch('/owner/role-permissions', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-role-permissions'] }),
  });
}

// === เบี้ยขยันขั้นบันได (diligence ladder) ===
export interface DiligenceLadder {
  id?: string;
  name?: string;
  active: boolean;
  steps: number[];
  start_tier: number;
  break_on_late: boolean;
  break_on_absent: boolean;
  break_leave_types: string[];
  is_taxable: boolean;
  is_ssoable: boolean;
  is_pfable: boolean;
}
export function useDiligenceLadder() {
  return useQuery({
    queryKey: ['diligence-ladder'],
    queryFn: () => apiFetch<{ config: DiligenceLadder | null }>('/hr/diligence-ladder').then((d) => d.config),
  });
}
export function useSaveDiligenceLadder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DiligenceLadder) =>
      apiFetch<{ config: DiligenceLadder }>('/hr/diligence-ladder', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['diligence-ladder'] }),
  });
}

// === เบี้ยขยันยอดคงที่ (รอบค่าคอม) + ยืนยันใบรับรองแพทย์ ===
export interface DiligenceSettings {
  DILIGENCE_FLAT_AMOUNT: string;
  /** คั่นด้วยจุลภาค · ว่าง = ลาทุกประเภทตัดเบี้ยขยัน */
  DILIGENCE_ALLOWED_LEAVE: string;
  /** อ่านอย่างเดียว · null = ยังไม่เปิดกฎใหม่ */
  ATTENDANCE_RULES_FROM: string | null;
  available: boolean;
  warning?: string;
}
export interface MedicalCertItem {
  request_group_id: string;
  leave_id: string;
  emp_code: string;
  name: string | null;
  nickname: string | null;
  department: string | null;
  dates: string[];
  status: string;
  verified: boolean;
  verified_by: string | null;
  verified_by_name: string | null;
  verified_at: string | null;
  evidence_url: string | null;
}
export interface MedicalCertList {
  period: string;
  window_start: string;
  window_end: string;
  available: boolean;
  warning?: string;
  items: MedicalCertItem[];
}

/** ค่าที่ระบบคิดของรอบค่าคอมเปลี่ยนตามการตั้งค่า/การยืนยัน — ล้างเฉพาะตัวเลขเทียบ ไม่แตะรายการในรอบ */
function invalidatePayoutComputed(qc: QueryClient) {
  qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'payouts' && q.queryKey[3] === 'computed' });
}

export function useDiligenceSettings() {
  return useQuery({
    queryKey: ['diligence-settings'],
    queryFn: () => apiFetch<DiligenceSettings>('/hr/diligence-settings'),
  });
}
export function useSaveDiligenceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { DILIGENCE_FLAT_AMOUNT: string; DILIGENCE_ALLOWED_LEAVE: string }) =>
      apiFetch<{ updated: number }>('/hr/diligence-settings', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diligence-settings'] });
      // /owner/settings ส่งกลับ 2 คีย์นี้ทั้งฟอร์ม — ค่าค้างในแคชจะทับค่าใหม่ตอน Owner กดบันทึก
      qc.invalidateQueries({ queryKey: ['owner', 'settings'] });
      invalidatePayoutComputed(qc);
    },
  });
}
// === หักลืมสแกนหน้า ===
export interface ForgetScanStatus {
  period: string;
  /** ลืมสแกนออกในรอบนี้ */
  cycle_count: number;
  cycle_in_count: number;
  cumulative_count: number;
  warning: boolean;
  warning_at: number;
}
export interface ForgetScanWarningRow {
  emp_code: string;
  name: string;
  department: string | null;
  cycle_count: number;
  cumulative_count: number;
  /** การ์ดที่ยังเปิดของงวดนี้ · null = ไม่มีการ์ดให้ปิด */
  escalation_id: string | null;
}
export interface ForgetScanWarnings {
  period: string;
  available: boolean;
  warning_at: number;
  cumulative: boolean;
  warning?: string;
  rows: ForgetScanWarningRow[];
}
export interface ForgetScanSettingsValues {
  forget_in_amount: number;
  forget_out_low_amount: number;
  forget_out_low_count: number;
  forget_out_high_amount: number;
  /** 0 = ปิดการแจ้งเตือน */
  forget_warning_at: number;
  forget_warning_cumulative: boolean;
}
export interface ForgetScanSettings extends ForgetScanSettingsValues {
  /** อ่านอย่างเดียว · null = ยังไม่เปิดกฎใหม่ */
  attendance_rules_from: string | null;
  available: boolean;
  warning?: string;
}
/**
 * ยอดของ popup แจ้งเตือน — อ่านไม่ได้ต้องเงียบ และ AdminLayout/Home mount ใหม่ทุกครั้งที่เปลี่ยนหน้า
 * จึงไม่ retry และไม่ยิงซ้ำตอน mount เมื่อครั้งก่อนล้มเหลว (ไม่งั้น 403 วิ่งทุกหน้า)
 */
const FORGET_POPUP_QUERY = { staleTime: 30 * 60_000, retry: false, retryOnMount: false } as const;

export function useMyForgetScanStatus(enabled: boolean) {
  const profile = useAuthStore((st) => st.profile);
  return useQuery({
    queryKey: ['me', 'forget-scan-status', profile?.tenant_id ?? null, profile?.emp_code ?? null],
    queryFn: () => apiFetch<ForgetScanStatus>('/me/forget-scan-status'),
    enabled,
    ...FORGET_POPUP_QUERY,
  });
}
export function useForgetScanWarnings(enabled: boolean) {
  const profile = useAuthStore((st) => st.profile);
  return useQuery({
    // ขึ้นต้นด้วย 'escalations' เพื่อให้ useResolveEscalation รีเฟรชรายการนี้ด้วย ไม่ว่าจะปิดจากหน้าไหน
    queryKey: ['escalations', 'forget-scan-warnings', profile?.tenant_id ?? null, profile?.user_id ?? null],
    queryFn: () => apiFetch<ForgetScanWarnings>('/hr/forget-scan-warnings'),
    enabled,
    ...FORGET_POPUP_QUERY,
  });
}
export function useForgetScanSettings() {
  return useQuery({
    queryKey: ['forget-scan-settings'],
    queryFn: () => apiFetch<ForgetScanSettings>('/hr/forget-scan-settings'),
  });
}
export function useSaveForgetScanSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ForgetScanSettingsValues>) =>
      apiFetch<{ updated: number }>('/hr/forget-scan-settings', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forget-scan-settings'] });
      qc.invalidateQueries({ queryKey: ['escalations', 'forget-scan-warnings'] });
      qc.invalidateQueries({ queryKey: ['me', 'forget-scan-status'] });
      qc.invalidateQueries({ queryKey: ['owner', 'docking-review'] });
      invalidatePayoutComputed(qc);
    },
  });
}
export function useMedicalCerts(period: string, enabled = true) {
  return useQuery({
    queryKey: ['medical-certs', period],
    queryFn: () => apiFetch<MedicalCertList>(`/hr/leave/medical-certs?period=${encodeURIComponent(period)}`),
    enabled: enabled && /^\d{4}-\d{2}$/.test(period),
    // ลิงก์ไฟล์ที่ worker sign หมดอายุใน 5 นาที — ต้องขอใหม่ก่อนหมด ไม่งั้น "ดูไฟล์" เปิดไม่ขึ้น
    refetchInterval: 4 * 60_000,
    refetchOnWindowFocus: true,
  });
}
export function useVerifyMedicalCert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { leave_id: string; verified: boolean }) =>
      apiFetch<{ request_group_id: string; verified: boolean; updated: number }>(
        `/hr/leave/${encodeURIComponent(args.leave_id)}/medical-cert`,
        { method: 'POST', body: { verified: args.verified } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['medical-certs'] });
      invalidatePayoutComputed(qc);
    },
  });
}

// === ทีมของฉัน (ผู้ใต้บังคับบัญชา) ===
export interface Subordinate {
  emp_code: string;
  name: string;
  nickname: string | null;
  position: string | null;
  department: string | null;
  status: string;
  phone: string | null;
}
export function useMySubordinates(enabled = true) {
  return useQuery({
    queryKey: ['my-subordinates'],
    queryFn: () => apiFetch<{ subordinates: Subordinate[] }>('/me/subordinates'),
    staleTime: 60_000,
    enabled,
  });
}

// รายชื่อ "หัวหน้า/ผู้จัดการ" สำหรับเลือกเป็นผู้อนุมัติ (ไม่รวมพนักงานทั่วไป)
export interface SupervisorCandidate {
  emp_code: string;
  name: string;
  position: string | null;
  department: string | null;
}
export function useSupervisorCandidates() {
  return useQuery({
    queryKey: ['supervisor-candidates'],
    queryFn: () => apiFetch<{ candidates: SupervisorCandidate[] }>('/hr/supervisor-candidates').then((d) => d.candidates),
    staleTime: 60_000,
  });
}

// ตั้งหัวหน้างาน (ผู้อนุมัติลา/OT) ให้พนักงานหลายคนพร้อมกัน — supervisor_emp_code=null = ล้างผู้อนุมัติ
export function useBulkSetSupervisor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { supervisor_emp_code: string | null; emp_codes: string[] }) =>
      apiFetch<{ ok: boolean; set: number; skipped_self: number }>(
        '/hr/bulk-set-supervisor', { method: 'POST', body: args },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
      qc.invalidateQueries({ queryKey: ['blueprint'] });
    },
  });
}

// === My capabilities (HR delegation) — ใช้ซ่อน/แสดงเมนูที่ gate ด้วย requireCapability ===
export function useMyCapabilities() {
  return useQuery({
    queryKey: ['my-capabilities'],
    queryFn: () => apiFetch<{ capabilities: string[] }>('/me/capabilities').then((d) => d.capabilities),
    staleTime: 60_000,
  });
}

/** ความสามารถที่ระดับมอบอำนาจ HR ตั้งเป็น "ไม่ได้" — ใช้ซ่อนเมนู/ปุ่มที่หลังบ้านปฏิเสธแน่ ๆ
 *  (OWNER/บทบาทอื่น = ว่าง). ยังโหลดไม่เสร็จ = ว่าง (หลังบ้านยังกันอยู่ดี). */
export function useMyDeniedCapabilities(enabled = true) {
  // key แยกตามผู้ใช้+บริษัท — สลับบัญชี/ออกแล้วเข้าใหม่ในแท็บเดิมจะไม่ได้ค่าของคนก่อน
  const profile = useAuthStore((st) => st.profile);
  return useQuery({
    queryKey: ['my-capabilities', 'denied', profile?.tenant_id ?? null, profile?.user_id ?? null],
    queryFn: () => apiFetch<{ denied?: string[] }>('/me/capabilities').then((d) => d.denied ?? []),
    staleTime: 60_000,
    enabled: enabled && !!profile,
  });
}

// === Salary analysis (วิเคราะห์เงินเดือน) — OWNER + HR เต็มสิทธิ์ (edit_salary) ===
export interface SalaryAnalysisRow {
  emp_code: string;
  name: string;
  department: string | null;
  position: string | null;
  base_salary: number;
  band: { min: number; mid: number; max: number } | null;
  compa_ratio: number | null;
  zone: 'below' | 'within' | 'above' | null;
}
export interface SalaryAnalysis {
  generated_at: string;
  summary: {
    headcount: number; total_base: number; avg: number; median: number; min: number; max: number;
    compa: { below: number; within: number; above: number; no_band: number };
  };
  by_department: Array<{ department: string; count: number; total: number; avg: number; min: number; max: number }>;
  rows: SalaryAnalysisRow[];
}
export function useSalaryAnalysis(enabled = true) {
  return useQuery({
    queryKey: ['salary-analysis'],
    queryFn: () => apiFetch<SalaryAnalysis>('/owner/salary/analysis'),
    enabled,
    staleTime: 30_000,
    retry: false, // 403 (สิทธิ์ไม่พอ) ไม่ต้อง retry
  });
}

// === Org structure (departments + positions, rich/hierarchy) — SoT (ADR 0003) ===
// PayPilot owns the org tables; these hit /hr/org/*. Eval reads them as overlay.
export interface OrgDepartment {
  id: string;
  tenant_id: string;
  name: string;
  parent_department_id: string | null;
  description: string | null;
  created_at?: string;
  updated_at?: string;
}
export interface OrgPosition {
  id: string;
  tenant_id: string;
  title: string;
  department_id: string | null;
  level: number;
  parent_position_id: string | null;
  qualifications?: Record<string, unknown>;
  holding_criteria?: Record<string, unknown>;
  headcount_target?: number;
  description: string | null;
  created_at?: string;
  updated_at?: string;
}
export function useOrgDepartments() {
  return useQuery({
    queryKey: ['org-departments'],
    queryFn: () => apiFetch<OrgDepartment[]>('/hr/org/departments'),
    staleTime: 60_000,
  });
}
export function useSaveOrgDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<OrgDepartment> & { id?: string }) =>
      id
        ? apiFetch<OrgDepartment>(`/hr/org/departments/${id}`, { method: 'PATCH', body })
        : apiFetch<OrgDepartment>('/hr/org/departments', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-departments'] }),
  });
}
export function useDeleteOrgDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/org/departments/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-departments'] }),
  });
}
export function useOrgPositions() {
  return useQuery({
    queryKey: ['org-positions'],
    queryFn: () => apiFetch<OrgPosition[]>('/hr/org/positions'),
    staleTime: 60_000,
  });
}
export function useSaveOrgPosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<OrgPosition> & { id?: string }) =>
      id
        ? apiFetch<OrgPosition>(`/hr/org/positions/${id}`, { method: 'PATCH', body })
        : apiFetch<OrgPosition>('/hr/org/positions', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-positions'] }),
  });
}
export function useDeleteOrgPosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/org/positions/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-positions'] }),
  });
}

export function useSendSlips() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string }) =>
      apiFetch('/owner/payroll/send-slips', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

// === Owner: 7-stage pipeline additions (Phase 1A.1) ===
export interface PayrollPreviewEmployee {
  emp_code: string;
  gross: number;
  deductions_total: number;
  net: number;
  prev_net: number | null;
  delta_pct: number | null;
  anomalies: string[];
}
export interface PayrollPreviewResult {
  period: string;
  status: 'previewed';
  totals: { gross_sum: number; deductions_sum: number; net_sum: number };
  prev_totals: { gross_sum: number; deductions_sum: number; net_sum: number };
  prev_period: string;
  employees: PayrollPreviewEmployee[];
}

export function usePreviewPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string }) =>
      apiFetch<PayrollPreviewResult>('/owner/payroll/preview', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

export function useApprovePayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string }) =>
      apiFetch<{ period: string; mode: 'owner' | 'hr_backup' | 'solo' }>(
        '/owner/payroll/approve',
        { method: 'POST', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

export function useRejectPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; reason: string }) =>
      apiFetch('/owner/payroll/reject', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

// M5 audit — Owner-only undo of a locked/pdf_ready/sending/partial_sent/sent
// period. Reverts to 'approved' server-side; `warning` is non-null when
// slips already reached employees (must be re-sent after re-locking).
export interface PayrollUnlockResult {
  period: string;
  status: 'approved';
  rows: number;
  already_sent_count: number;
  warning: string | null;
}

export function useUnlockPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; reason: string }) =>
      apiFetch<PayrollUnlockResult>('/owner/payroll/unlock', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

// === Deliveries tracking (Phase 1A.2) ===
export interface PayslipDelivery {
  id: string;
  emp_code: string;
  push_status: 'pending' | 'sent' | 'failed' | 'blocked';
  push_attempts: number;
  push_last_error: string | null;
  push_sent_at: string | null;
}

export function useDeliveries(period: string | null) {
  return useQuery({
    queryKey: ['deliveries', period],
    queryFn: () => apiFetch<PayslipDelivery[]>(`/owner/payroll/deliveries?period=${period}`),
    enabled: !!period,
    refetchInterval: (q) => {
      const rows = q.state.data as PayslipDelivery[] | undefined;
      // Poll while any delivery is in-flight or retryable
      return rows?.some((d) => d.push_status === 'pending' || d.push_status === 'failed')
        ? 5_000
        : false;
    },
  });
}

export function useRetryDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deliveryId: string) =>
      apiFetch<{ id: string; push_status: string; error?: string }>(
        `/owner/payroll/deliveries/${encodeURIComponent(deliveryId)}/retry`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliveries'] }),
  });
}

// === Phase 1B — Allowance master + eligibility ===
export interface AllowanceMasterRow {
  id: string;
  code: string;
  name_th: string;
  preset_type: string;
  is_taxable: boolean;
  is_ssoable: boolean;
  is_pfable: boolean;
  frequency: 'fixed_monthly' | 'variable';
  default_amount: number | null;
  default_rate: number | null;
  rate_unit: string | null;
  condition_type: 'perfect_attendance' | null;
  active: boolean;
}

export function useAllowanceMaster() {
  return useQuery({
    queryKey: ['allowance-master'],
    queryFn: () => apiFetch<AllowanceMasterRow[]>('/hr/allowance-master'),
  });
}
export function useUpsertAllowanceMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AllowanceMasterRow>) =>
      apiFetch<AllowanceMasterRow>('/hr/allowance-master', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allowance-master'] }),
  });
}
export function useDeleteAllowanceMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/allowance-master/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allowance-master'] }),
  });
}

export interface AllowanceEligibilityRow {
  id: string;
  emp_code: string;
  allowance_id: string;
  fixed_amount: number | null;
  rate: number | null;
  start_date: string;
  end_date: string | null;
  active: boolean;
}
export function useAllowanceEligibility(empCode: string | null) {
  return useQuery({
    queryKey: ['allowance-elig', empCode],
    queryFn: () =>
      apiFetch<AllowanceEligibilityRow[]>(
        `/hr/allowance-eligibility${empCode ? `?emp=${encodeURIComponent(empCode)}` : ''}`,
      ),
    enabled: empCode !== undefined,
  });
}
export function useAllowanceEligibilityByAllowance(allowanceId: string | null) {
  return useQuery({
    queryKey: ['allowance-elig-by', allowanceId],
    queryFn: () => apiFetch<AllowanceEligibilityRow[]>(`/hr/allowance-eligibility?allowance=${encodeURIComponent(allowanceId!)}`),
    enabled: !!allowanceId,
  });
}
export function useUpsertAllowanceEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AllowanceEligibilityRow>) =>
      apiFetch<AllowanceEligibilityRow>('/hr/allowance-eligibility', { method: 'POST', body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['allowance-elig'] }); qc.invalidateQueries({ queryKey: ['allowance-elig-by'] }); },
  });
}
export function useDeleteAllowanceEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/allowance-eligibility/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['allowance-elig'] }); qc.invalidateQueries({ queryKey: ['allowance-elig-by'] }); },
  });
}

// === Recurring deduction master + eligibility ===
export interface DeductionMasterRow {
  id: string;
  code: string;
  name_th: string;
  kind: 'statutory' | 'fixed';
  default_amount: number | null;
  note: string | null;
  active: boolean;
}
export function useDeductionMaster() {
  return useQuery({
    queryKey: ['deduction-master'],
    queryFn: () => apiFetch<DeductionMasterRow[]>('/hr/deduction-master'),
  });
}
export function useUpsertDeductionMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<DeductionMasterRow>) =>
      apiFetch<DeductionMasterRow>('/hr/deduction-master', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deduction-master'] }),
  });
}
export function useDeleteDeductionMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/deduction-master/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deduction-master'] }),
  });
}

export interface DeductionEligibilityRow {
  id: string;
  emp_code: string;
  deduction_id: string;
  amount: number | null;
  start_date: string;
  end_date: string | null;
  active: boolean;
}
export function useDeductionEligibility(params: { emp?: string | null; deduction?: string | null }) {
  const { emp, deduction } = params;
  return useQuery({
    queryKey: ['deduction-elig', emp ?? null, deduction ?? null],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (emp) qs.set('emp', emp);
      if (deduction) qs.set('deduction', deduction);
      const q = qs.toString();
      return apiFetch<DeductionEligibilityRow[]>(`/hr/deduction-eligibility${q ? `?${q}` : ''}`);
    },
    enabled: emp !== undefined || deduction !== undefined,
  });
}
export function useUpsertDeductionEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<DeductionEligibilityRow>) =>
      apiFetch<DeductionEligibilityRow>('/hr/deduction-eligibility', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deduction-elig'] }),
  });
}
export function useDeleteDeductionEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/deduction-eligibility/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deduction-elig'] }),
  });
}

// === Phase 1B — Data entry per period ===
export interface DataEntryRow {
  id: string;
  period: string;
  emp_code: string;
  entry_type: 'variable_allowance' | 'adjustment_add' | 'adjustment_deduct';
  allowance_id: string | null;
  qty: number | null;
  adjustment_label: string | null;
  amount: number;
  /** preset ภาษี/ปกส. ของรายการปรับ — ต้องมีเพื่อให้โหมดแก้ไขคืนค่าเดิมได้ถูก */
  adjustment_preset: 'bonus' | 'wage_correction' | 'reimbursement' | null;
  notes: string | null;
  updated_at: string;
}

export function useDataEntries(period: string | null) {
  return useQuery({
    queryKey: ['data-entries', period],
    queryFn: () => apiFetch<DataEntryRow[]>(`/owner/payroll/data-entry?period=${period}`),
    enabled: !!period,
  });
}
export function useUpsertVariableAllowanceEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; emp_code: string; allowance_id: string; qty: number }) =>
      apiFetch('/owner/payroll/data-entry/variable-allowance', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-entries'] }),
  });
}
export type AdjustmentPreset = 'bonus' | 'wage_correction' | 'reimbursement';
export function useUpsertAdjustmentEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; emp_code: string; direction: 'add' | 'deduct'; label: string; amount: number; preset?: AdjustmentPreset; notes?: string }) =>
      apiFetch('/owner/payroll/data-entry/adjustment', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-entries'] }),
  });
}
export interface BulkAdjustmentEntry {
  emp_code: string;
  direction: 'add' | 'deduct';
  label: string;
  amount: number;
  preset?: AdjustmentPreset;
}
export function useBulkUpsertAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    // clear_emp_codes = ทุกคนที่อยู่ในไฟล์ (รวมคนที่กรอก 0 ทุกช่อง) — หลังบ้านใช้เป็น
    // ขอบเขตล้างรายการของรอบก่อน ไม่งั้นคนที่ถูกลบยอดออกจากไฟล์จะยังโดนหักของเก่า.
    mutationFn: (body: { period: string; entries: BulkAdjustmentEntry[]; clear_emp_codes?: string[] }) =>
      apiFetch<{ count: number; cleared?: number; period: string }>('/owner/payroll/data-entry/adjustment/bulk', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-entries'] }),
  });
}
export function useDeleteDataEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/owner/payroll/data-entry/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-entries'] }),
  });
}

// === Owner: Payroll pay override (กำหนดยอดจ่ายงวดนี้ — ข้ามการคิดอัตโนมัติ) ===
export interface PayOverrideRow {
  id: string;
  period: string;
  emp_code: string;
  amount: number;
  source: string;
  notes: string | null;
  updated_at: string;
}
export function usePayOverrides(period: string) {
  return useQuery({
    queryKey: ['pay-overrides', period],
    queryFn: () => apiFetch<PayOverrideRow[]>(`/owner/payroll/pay-override?period=${encodeURIComponent(period)}`),
    enabled: !!period,
  });
}
export function useSetPayOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; emp_code: string; amount: number; source?: string; notes?: string }) =>
      apiFetch('/owner/payroll/pay-override', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pay-overrides'] }),
  });
}
export interface BulkPayOverrideEntry {
  emp_code: string;
  amount: number;
}
export function useBulkSetPayOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; entries: BulkPayOverrideEntry[] }) =>
      apiFetch<{ count: number; period: string }>('/owner/payroll/pay-override/bulk', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pay-overrides'] }),
  });
}

// Per-employee payroll breakdown for the Run Draft detail drawer.
export interface PayrollRunDetail {
  emp_code: string;
  status: string;
  computed_at: string | null;
  gross: number;
  deductions_total: number;
  net: number;
  earnings: Array<{ label: string; amount: number }>;
  deductions: Array<{ label: string; amount: number }>;
  earnings_total: number;
  deductions_line_total: number;
  ytd: { gross?: number; tax?: number; sso?: number; pf?: number; net?: number } | null;
}
export function usePayrollRunDetail(period: string | null, empCode: string | null) {
  return useQuery({
    queryKey: ['payroll-run-detail', period, empCode],
    queryFn: () => apiFetch<PayrollRunDetail>(
      `/owner/payroll/run-detail?period=${encodeURIComponent(period!)}&emp_code=${encodeURIComponent(empCode!)}`,
    ),
    enabled: !!period && !!empCode,
  });
}

// === Owner: Audit Log ===
export function useAuditLog(limit = 50) {
  return useQuery({
    queryKey: ['audit-log', limit],
    queryFn: () => apiFetch(`/owner/audit-log?limit=${limit}`),
  });
}

// === Owner: Deductions ===
export function useDeductions() {
  return useQuery({
    queryKey: ['owner', 'deductions'],
    queryFn: () => apiFetch('/owner/deductions'),
  });
}

export function useUpsertDeduction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/owner/deductions', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner', 'deductions'] }),
  });
}

// === Owner: Dashboard summary (KPI counts) ===
export interface DashboardSummary {
  employees_active: number;
  pending_approvals: number;
  pending_leaves: number;
  pending_ots: number;
  today_checkins: number;
  today_leaves: number;
  pending_escalations: number;
  pending_changes: number;
}
export function useOwnerDashboardSummary() {
  return useQuery({
    queryKey: ['owner', 'dashboard-summary'],
    queryFn: () => apiFetch<DashboardSummary>('/owner/dashboard-summary'),
    refetchInterval: 60_000,
  });
}

// === Self profile (employee) ===
export interface ProfileResponse {
  profile: Record<string, unknown> & {
    emp_code: string;
    first_name: string | null;
    last_name: string | null;
    department: string | null;
    position: string | null;
    national_id: string | null;
    birth_date: string | null;
    phone: string | null;
    sso_number: string | null;
    bank: string | null;
    bank_account_masked: string | null;
    id_copy_path: string | null;
    bankbook_path: string | null;
    id_copy_url: string | null;
    bankbook_url: string | null;
    address_current: string | null;
    address_registered: string | null;
    emergency_name: string | null;
    emergency_relationship: string | null;
    emergency_phone: string | null;
  };
  completion_pct: number;
  missing_sections: string[];
}
export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => apiFetch<ProfileResponse>('/me/profile'),
  });
}
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, string | undefined>) =>
      apiFetch<{ completion_pct: number; saved: string[] }>('/me/profile', {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });
}

// === Owner: 7-day trend stats ===
export interface DailyStats { date: string; checkins: number; leaves: number; ots: number }
export function useLast7DaysStats() {
  return useQuery({
    queryKey: ['owner', 'stats', 'last7days'],
    queryFn: () => apiFetch<DailyStats[]>('/owner/stats/last7days'),
    refetchInterval: 5 * 60_000,
  });
}

// === Owner: Settings (key-value config) ===
export function useSettings() {
  return useQuery({
    queryKey: ['owner', 'settings'],
    queryFn: () => apiFetch<Record<string, string>>('/owner/settings'),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiFetch<{ updated: number }>('/owner/settings', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner', 'settings'] });
      qc.invalidateQueries({ queryKey: ['checkin-status'] });
    },
  });
}

// === HR: Geofence-only settings (subset of /owner/settings, HR-accessible) ===
// The dedicated GPS page is HR+OWNER, but /owner/settings is OWNER-only. These
// hooks hit the HR-scoped geofence endpoints so HR can load/save the office-GPS
// fields without touching payday/cutoff/statutory config.
export function useGeofenceSettings() {
  return useQuery({
    queryKey: ['owner', 'geofence-settings'],
    queryFn: () => apiFetch<Record<string, string>>('/owner/office-location/settings'),
  });
}

export function useUpdateGeofenceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiFetch<{ updated: number }>('/owner/office-location/settings', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner', 'geofence-settings'] });
      qc.invalidateQueries({ queryKey: ['owner', 'settings'] });
      qc.invalidateQueries({ queryKey: ['checkin-status'] });
    },
  });
}

// Company identity used on official documents (payslip, 50ทวิ, …).
export interface CompanyProfile {
  company_name: string;
  company_address: string;
  tax_id: string;
  company_phone: string;
  signer_name: string;
  signer_title: string;
  /** Raw R2 path — round-trip with server on save (logo_path field). */
  logo_path: string;
  /** Signed URL for display in <img>. Empty when no logo set. */
  logo_url: string;
}
export function useCompanyProfile() {
  return useQuery({
    queryKey: ['owner', 'company-profile'],
    queryFn: () => apiFetch<CompanyProfile>('/owner/company-profile'),
  });
}
export function useUpdateCompanyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CompanyProfile>) =>
      apiFetch<{ updated: number }>('/owner/company-profile', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner', 'company-profile'] }),
  });
}

// === Owner: พิกัดออฟฟิศที่ระบบเรียนรู้จากการเช็คอินจริง ===
export interface OfficeLocationSuggestion {
  lat: number | null;
  lng: number | null;
  radius_m: number | null;
  sample_employees: number;
  sample_events: number;
  sample_days: number;
  p90_dist_m: number | null;
  confident: boolean;
  reason?: string;
}
export interface OfficeLocationSuggestionResp {
  suggestion: OfficeLocationSuggestion | null;
  current: { lat: number | null; lng: number | null; radius_m: number; mode: 'off' | 'observe' | 'enforce' };
}
export function useOfficeLocationSuggestion() {
  return useQuery({
    queryKey: ['owner', 'office-location-suggestion'],
    queryFn: () => apiFetch<OfficeLocationSuggestionResp>('/owner/office-location/suggestion'),
    staleTime: 60_000,
  });
}
export function useApplyOfficeLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { lat: number; lng: number; radius_m: number }) =>
      apiFetch<{ ok: boolean }>('/owner/office-location/apply', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner', 'office-location-suggestion'] });
      qc.invalidateQueries({ queryKey: ['owner', 'settings'] });
      qc.invalidateQueries({ queryKey: ['checkin-status'] });
    },
  });
}

// === HR: สาขา / จุดเช็คอินเพิ่มเติม (work_sites) ===
export interface WorkSite {
  id: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  radius_m: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}
export type WorkSiteInput = { name: string; address: string; latitude: number; longitude: number; radius_m: number };

export function useWorkSites() {
  return useQuery({
    queryKey: ['owner', 'work-sites'],
    queryFn: () => apiFetch<{ sites: WorkSite[]; ready: boolean }>('/owner/work-sites'),
  });
}
export function useSaveWorkSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<WorkSiteInput> & { id?: string; active?: boolean }) =>
      apiFetch<{ site: WorkSite }>(id ? `/owner/work-sites/${id}` : '/owner/work-sites', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner', 'work-sites'] });
      qc.invalidateQueries({ queryKey: ['checkin-status'] });
    },
  });
}
export function useResolveMapsLink() {
  return useMutation({
    mutationFn: (url: string) => apiFetch<{ lat: number; lng: number }>('/owner/work-sites/resolve-link', { method: 'POST', body: { url } }),
  });
}

// === Owner: Drive ===
export function useDriveStatus() {
  return useQuery({
    queryKey: ['drive-status'],
    queryFn: () => apiFetch('/owner/drive/status'),
    refetchInterval: 60_000,
  });
}

export function useConfigureDrive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { drive_root_id: string }) =>
      apiFetch('/owner/drive/configure', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drive-status'] }),
  });
}

export function useForceDriveSync() {
  return useMutation({
    mutationFn: () => apiFetch('/owner/drive/force-sync', { method: 'POST', body: {} }),
  });
}

export function useForceSheetSync() {
  return useMutation({
    mutationFn: () => apiFetch('/owner/sheet/force-sync', { method: 'POST', body: {} }),
  });
}

// === Owner: Onboard Employee ===
export function useOnboardEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ emp_code: string; pairing_code: string; expires_at: string }>(
        '/owner/onboard-employee',
        { method: 'POST', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'employees'] }),
  });
}

// === Escalations ===
export function useEscalations() {
  return useQuery({
    queryKey: ['escalations'],
    queryFn: () => apiFetch('/escalations'),
  });
}

// ปิด/ตรวจแล้ว รายการที่ต้องตรวจสอบ (สรุปเวลา) → refetch ให้การ์ดหายทันที
export function useResolveEscalation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { esc_id: string; resolution: 'resolved' | 'ignored'; note?: string }) =>
      apiFetch<{ esc_id: string; status: string }>('/escalations/resolve', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['escalations'] }),
  });
}

// === Education + Work History (per employee) ===
export interface EducationRow {
  id: string;
  degree: string;
  institution: string;
  major: string | null;
  year_start: number | null;
  year_end: number | null;
  gpa: number | null;
  note: string | null;
}
export interface WorkHistoryRow {
  id: string;
  company: string;
  position: string;
  start_date: string | null;
  end_date: string | null;
  reason_for_leaving: string | null;
  note: string | null;
}

// Self-service (path /me/profile/...)
export function useOwnEducation() {
  return useQuery({
    queryKey: ['me', 'education'],
    queryFn: () => apiFetch<EducationRow[]>('/me/profile/education'),
  });
}
export function useUpsertOwnEducation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<EducationRow>) =>
      apiFetch<EducationRow>('/me/profile/education', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'education'] }),
  });
}
export function useDeleteOwnEducation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/me/profile/education/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'education'] }),
  });
}
export function useOwnWorkHistory() {
  return useQuery({
    queryKey: ['me', 'work-history'],
    queryFn: () => apiFetch<WorkHistoryRow[]>('/me/profile/work-history'),
  });
}
export function useUpsertOwnWorkHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<WorkHistoryRow>) =>
      apiFetch<WorkHistoryRow>('/me/profile/work-history', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'work-history'] }),
  });
}
export function useDeleteOwnWorkHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/me/profile/work-history/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'work-history'] }),
  });
}

// HR admin (path /hr/employees/:emp/...)
export function useEmployeeEducation(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'education', empCode],
    queryFn: () =>
      apiFetch<EducationRow[]>(`/hr/employees/${encodeURIComponent(empCode!)}/education`),
    enabled: !!empCode,
  });
}
export function useUpsertEmployeeEducation(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<EducationRow>) =>
      apiFetch<EducationRow>(`/hr/employees/${encodeURIComponent(empCode)}/education`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'education', empCode] }),
  });
}
export function useDeleteEmployeeEducation(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/education/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'education', empCode] }),
  });
}
export function useEmployeeWorkHistory(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'work-history', empCode],
    queryFn: () =>
      apiFetch<WorkHistoryRow[]>(`/hr/employees/${encodeURIComponent(empCode!)}/work-history`),
    enabled: !!empCode,
  });
}
export function useUpsertEmployeeWorkHistory(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<WorkHistoryRow>) =>
      apiFetch<WorkHistoryRow>(`/hr/employees/${encodeURIComponent(empCode)}/work-history`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'work-history', empCode] }),
  });
}
export function useDeleteEmployeeWorkHistory(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/work-history/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'work-history', empCode] }),
  });
}

// === Employee documents (N file attachments per employee) ===
// import + re-export จาก shared (single source of truth) — เดิม hardcode ที่นี่ทำให้
// type ไม่ตรงเวลาเพิ่มประเภทใหม่ (transcript/ใบตรวจร่างกาย/สัญญาแนบท้าย ฯลฯ)
import type { EmployeeDocType } from '@payroll/shared';
export type { EmployeeDocType };

export interface DocumentRow {
  id: string;
  label: string;
  r2_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  note: string | null;
  uploaded_at: string;
  url: string;        // worker-signed
  doc_type?: EmployeeDocType | null;
  version?: number;
  is_current?: boolean;
  sign_status?: 'awaiting' | 'signed' | null;
  sign_method?: 'onscreen' | 'upload' | null;
  signed_at?: string | null;
  generated?: boolean;
  signature_url?: string | null;
  signed_url?: string | null;
}

interface CreateDocumentBody {
  label: string;
  r2_path: string;
  mime_type?: string;
  size_bytes?: number;
  note?: string;
  doc_type?: EmployeeDocType;
}

export function useOwnDocuments() {
  return useQuery({
    queryKey: ['me', 'documents'],
    queryFn: () => apiFetch<DocumentRow[]>('/me/profile/documents'),
  });
}
export function useCreateOwnDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDocumentBody) =>
      apiFetch<DocumentRow>('/me/profile/documents', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'documents'] }),
  });
}
export function useDeleteOwnDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/me/profile/documents/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'documents'] }),
  });
}

export function useEmployeeDocuments(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'documents', empCode],
    queryFn: () =>
      apiFetch<DocumentRow[]>(`/hr/employees/${encodeURIComponent(empCode!)}/documents`),
    enabled: !!empCode,
  });
}
export function useCreateEmployeeDocument(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDocumentBody) =>
      apiFetch<DocumentRow>(`/hr/employees/${encodeURIComponent(empCode)}/documents`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'documents', empCode] }),
  });
}
export function useDeleteEmployeeDocument(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/documents/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'documents', empCode] });
      qc.invalidateQueries({ queryKey: ['hr', 'change-history', empCode] });
    },
  });
}

// ประวัติแก้ข้อมูลรายคน (เดิม → ใหม่) จาก audit_log
export interface ChangeHistoryRow {
  id: string;
  timestamp: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  actor_name: string | null;
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
  restored?: boolean;
}
export function useEmployeeChangeHistory(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'change-history', empCode],
    queryFn: () => apiFetch<ChangeHistoryRow[]>(`/hr/employees/${encodeURIComponent(empCode!)}/change-history`),
    enabled: !!empCode,
  });
}
// เจ้าของกู้คืนไฟล์ที่ HR ลบ
export function useRestoreEmployeeDocument(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (auditId: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/documents/restore`, {
        method: 'POST',
        body: { audit_id: auditId },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'documents', empCode] });
      qc.invalidateQueries({ queryKey: ['hr', 'change-history', empCode] });
    },
  });
}

// แจ้งเตือนพนักงานคนเดียวให้เข้ามากรอกข้อมูล/แนบเอกสารให้ครบ (LINE)
export function useNudgeProfile() {
  return useMutation({
    mutationFn: (empCode: string) =>
      apiFetch<{ sent: boolean; reason?: string }>(
        `/hr/employees/${encodeURIComponent(empCode)}/nudge-profile`,
        { method: 'POST' },
      ),
  });
}

// === Documents overview + reminders + contract generation + signing ===
export interface DocumentsOverviewRow {
  emp_code: string;
  name: string;
  department: string | null;
  start_date: string | null;
  docs: Record<string, { present: boolean; uploaded_at: string | null; version: number | null }>;
  pending_sign: number;
  pending_sign_docs: { doc_type: string | null; label: string; version: number | null; uploaded_at: string | null }[];
}
export interface DocumentsOverview {
  employees: DocumentsOverviewRow[];
  required_types: EmployeeDocType[];
}
export function useDocumentsOverview() {
  return useQuery({
    queryKey: ['hr', 'documents-overview'],
    queryFn: () => apiFetch<DocumentsOverview>('/hr/documents/overview'),
  });
}

export function useRemindDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { emp_codes?: string[]; doc_types?: EmployeeDocType[] }) =>
      apiFetch<{ reminded: number; skipped_no_line: number }>('/hr/documents/remind', {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'documents-overview'] }),
  });
}

export function useGenerateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { emp_code: string; effective_date?: string; probation_months?: number; note?: string; notify?: boolean }) =>
      apiFetch<{ document: DocumentRow; pdf_url: string }>('/hr/contracts/generate', {
        method: 'POST',
        body,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hr', 'documents-overview'] });
      qc.invalidateQueries({ queryKey: ['hr', 'documents', vars.emp_code] });
    },
  });
}

export function useMyPendingSign() {
  return useQuery({
    queryKey: ['me', 'documents', 'pending-sign'],
    queryFn: () => apiFetch<Array<DocumentRow & { pdf_url: string }>>('/me/documents/pending-sign'),
  });
}

export function useSignDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; method: 'onscreen' | 'upload'; signature_r2_path?: string; signed_r2_path?: string }) =>
      apiFetch<DocumentRow>(`/me/documents/${encodeURIComponent(id)}/sign`, { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'documents', 'pending-sign'] });
      qc.invalidateQueries({ queryKey: ['me', 'documents'] });
    },
  });
}

// === Onboarding (เช็คลิสต์รับพนักงานใหม่ ตั้งค่าได้) ===
export function useOnboardingTemplate() {
  return useQuery({
    queryKey: ['hr', 'onboarding', 'template'],
    queryFn: () => apiFetch<{ items: OnboardingItem[] }>('/hr/onboarding/template').then((d) => d.items),
  });
}

export function useSaveOnboardingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: OnboardingItem[]) =>
      apiFetch<{ items: OnboardingItem[] }>('/hr/onboarding/template', { method: 'PUT', body: { items } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'onboarding', 'template'] });
      qc.invalidateQueries({ queryKey: ['hr', 'onboarding', 'overview'] });
    },
  });
}

export interface OnboardingOverviewRow {
  emp_code: string;
  name: string;
  department: string | null;
  done_count: number;
  required_count: number;
  start_date: string | null;
  status: string | null;
  items: OnboardingOverviewItem[];
  awaiting_sign: string[];
  overdue_count: number;
  next_due: OnboardingOverviewItem | null;
  /** ผูก LINE แล้วหรือยัง — ยังไม่ผูก = ทวงผ่านระบบไม่ได้ ต้องส่งคำเชิญทางอื่น */
  line_linked?: boolean;
}
export interface OnboardingOverviewItem {
  item_key: string;
  label: string;
  category: string;
  required: boolean;
  done: boolean;
  due_days: number | null;
  due_date: string | null;
  days_left: number | null;
  due_state: OnboardingDueState;
}
export interface OnboardingSummary {
  onboarding: number;
  awaiting_sign: number;
  overdue: number;
  due_soon: number;
  complete: number;
  no_start_date: number;
}
export function useOnboardingOverview() {
  return useQuery({
    queryKey: ['hr', 'onboarding', 'overview'],
    queryFn: () =>
      apiFetch<{
        employees: OnboardingOverviewRow[];
        categories: Record<string, string>;
        summary: OnboardingSummary;
        today: string;
      }>('/hr/onboarding/overview'),
  });
}

export interface OnboardingDetailItem {
  item_key: string;
  category: OnboardingCategory;
  label: string;
  doc_type: EmployeeDocType | null;
  auto_check: string | null;
  required: boolean;
  done: boolean;
  source: 'auto' | 'manual' | null;
  note: string | null;
  due_days: number | null;
  due_date: string | null;
  days_left: number | null;
  due_state: OnboardingDueState;
}
export function useOnboardingDetail(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'onboarding', empCode],
    enabled: !!empCode,
    queryFn: () =>
      apiFetch<{
        emp_code: string; name: string; status: string | null;
        start_date: string | null; awaiting_sign: string[];
        line_linked?: boolean;
        items: OnboardingDetailItem[];
      }>(`/hr/onboarding/${encodeURIComponent(empCode!)}`),
  });
}

export function useMarkOnboardingItem(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { item_key: string; done: boolean; note?: string }) =>
      apiFetch(`/hr/onboarding/${encodeURIComponent(empCode)}/item`, { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'onboarding', empCode] });
      qc.invalidateQueries({ queryKey: ['hr', 'onboarding', 'overview'] });
    },
  });
}

export function useStartOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (empCode: string) => apiFetch(`/hr/onboarding/${encodeURIComponent(empCode)}/start`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'onboarding', 'overview'] }),
  });
}

export function useCompleteOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (empCode: string) => apiFetch(`/hr/onboarding/${encodeURIComponent(empCode)}/complete`, { method: 'POST' }),
    onSuccess: (_d, empCode) => {
      qc.invalidateQueries({ queryKey: ['hr', 'onboarding', 'overview'] });
      qc.invalidateQueries({ queryKey: ['hr', 'onboarding', empCode] });
    },
  });
}

/** เอาออกจากรายการติดตาม (เพิ่มผิดคน/พนักงานทดสอบ) — ไม่ใช่ "รับเข้าสำเร็จ" */
export function useCancelOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (empCode: string) => apiFetch(`/hr/onboarding/${encodeURIComponent(empCode)}/cancel`, { method: 'POST' }),
    onSuccess: (_d, empCode) => {
      qc.invalidateQueries({ queryKey: ['hr', 'onboarding', 'overview'] });
      qc.invalidateQueries({ queryKey: ['hr', 'onboarding', empCode] });
    },
  });
}

export function useRemindOnboarding() {
  return useMutation({
    mutationFn: (empCode: string) =>
      apiFetch<{ reminded: boolean; skipped?: string; pending_count?: number }>(
        `/hr/onboarding/${encodeURIComponent(empCode)}/remind`,
        { method: 'POST' },
      ),
  });
}

export function useMyOnboarding() {
  return useQuery({
    queryKey: ['me', 'onboarding'],
    queryFn: () =>
      apiFetch<{
        status: string | null;
        items: Array<{ item_key: string; category: OnboardingCategory; label: string; doc_type: EmployeeDocType | null; required: boolean; done: boolean }>;
      }>('/me/onboarding'),
  });
}

// === กฎเอกสารตามอายุงาน (ตั้งค่าได้ทั้งหมด) ===
export interface DocumentRuleRow {
  rule_key: string;
  label: string;
  doc_type: string | null;
  anchor: 'start_date' | 'probation_end' | 'contract_end';
  offset_days: number;
  warn_days: number;
  required: boolean;
  active?: boolean;
  sort_order: number;
  note: string | null;
}
export interface DocumentRuleDueItem {
  emp_code: string;
  name: string;
  department: string | null;
  position: string | null;
  rule_key: string;
  rule_label: string;
  doc_type: string | null;
  doc_label: string | null;
  anchor: string;
  anchor_label: string;
  required: boolean;
  note: string | null;
  due_date: string | null;
  days_left: number | null;
  state: 'overdue' | 'due_today' | 'due_soon';
  cycle_key: string;
  probation_end: string | null;
  contract_end_date: string | null;
}

export function useDocumentRules() {
  return useQuery({
    queryKey: ['hr', 'document-rules'],
    queryFn: () =>
      apiFetch<{
        rules: DocumentRuleRow[];
        probation_days_default: number | null;
        anchors: Record<string, string>;
        doc_types: Record<string, string>;
      }>('/hr/document-rules'),
  });
}

export function useSaveDocumentRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { rules: DocumentRuleRow[]; probation_days_default?: number | null }) =>
      apiFetch('/hr/document-rules', { method: 'PUT', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'document-rules'] });
      qc.invalidateQueries({ queryKey: ['hr', 'document-rules', 'due'] });
    },
  });
}

export function useDocumentRulesDue() {
  return useQuery({
    queryKey: ['hr', 'document-rules', 'due'],
    queryFn: () =>
      apiFetch<{
        items: DocumentRuleDueItem[];
        today: string;
        probation_days_default: number | null;
        summary: { overdue: number; due_today: number; due_soon: number; missing_info: number };
        missing_info: Array<{ emp_code: string; name: string; missing: string[] }>;
      }>('/hr/document-rules/due'),
  });
}

export function useMarkDocumentRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { emp_code: string; rule_key: string; cycle_key: string; done: boolean; note?: string }) =>
      apiFetch(`/hr/document-rules/${encodeURIComponent(v.emp_code)}/mark`, {
        method: 'POST',
        body: { rule_key: v.rule_key, cycle_key: v.cycle_key, done: v.done, note: v.note },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'document-rules', 'due'] }),
  });
}

// === สถานะเซ็นสัญญาตามประเภท (บริการ 1 เดือน / ทดลองงาน / พนักงานประจำ) ===
export interface ContractSigningUnsignedEntry {
  emp_code: string;
  name: string;
  department: string | null;
  state: 'awaiting' | 'no_doc';
}
export interface ContractSigningTypeSummary {
  doc_type: string;
  label: string;
  signed_count: number;
  unsigned_count: number;
  unsigned: ContractSigningUnsignedEntry[];
}

export function useContractSigningSummary() {
  return useQuery({
    queryKey: ['hr', 'contract-signing', 'summary'],
    queryFn: () =>
      apiFetch<{ types: ContractSigningTypeSummary[]; total_employees: number }>('/hr/contract-signing/summary'),
  });
}

// === HR: Profile Followup (Day 7 / Day 14 pipeline) ===
export interface FollowupRow {
  emp_code: string;
  emp_name: string;
  profile_completion_pct: number;
  pair_completed_at: string;
  reminder_day7_sent_at: string | null;
  reminder_day14_sent_at: string | null;
  followup_status: 'active' | 'paused' | 'escalated' | 'completed';
  followup_completed_at: string | null;
  escalation_id: string | null;
  paused_at: string | null;
  pause_reason: string | null;
}

export function useFollowupList(status: 'active' | 'paused' | 'escalated' | 'completed' = 'active') {
  return useQuery({
    queryKey: ['hr', 'followup', status],
    queryFn: () => apiFetch<{ rows: FollowupRow[] }>(`/hr/followup/list?status=${status}`),
    refetchInterval: 60_000,
  });
}

export function usePauseFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; reason?: string }) =>
      apiFetch(`/hr/followup/${encodeURIComponent(args.emp_code)}/pause`, {
        method: 'POST',
        body: { reason: args.reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

export function useResumeFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string }) =>
      apiFetch(`/hr/followup/${encodeURIComponent(args.emp_code)}/resume`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

export function useSnoozeFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; days: number }) =>
      apiFetch(`/hr/followup/${encodeURIComponent(args.emp_code)}/snooze`, {
        method: 'POST',
        body: { days: args.days },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

export function useAdvanceFollowupClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; days: number }) =>
      apiFetch<{
        ok: boolean;
        emp_code: string;
        days_back: number;
        new_pair_completed_at: string;
      }>('/owner/test/advance-followup', { method: 'POST', body: args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

export function useRunFollowupNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ran: boolean; day7_sent: number; day14_sent: number; errors: number }>(
        '/owner/test/run-followup-now',
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

// === HR: Employee LINE pair history ===
export interface LineHistoryRow {
  id: string;
  line_user_id: string;
  started_at: string;
  ended_at: string | null;
  is_current: boolean;
  selfie_url: string | null;
  national_id_url: string | null;
  national_id_image_locked?: boolean;
}
export function useEmployeeLineHistory(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'line-history', empCode],
    queryFn: () =>
      apiFetch<{ emp_code: string; history: LineHistoryRow[] }>(
        `/hr/employees/line-history?emp_code=${encodeURIComponent(empCode!)}`,
      ),
    enabled: !!empCode,
  });
}

// === Reveal sensitive data (เรื่อง 3b) — เปิดดูทีละค่า, server เขียน audit ===
export function useRevealNationalId() {
  return useMutation({
    mutationFn: (emp_code: string) =>
      apiFetch<{ emp_code: string; national_id: string }>('/hr/reveal/national-id', { method: 'POST', body: { emp_code } }),
  });
}
export function useRevealNationalIdImage() {
  return useMutation({
    mutationFn: (emp_code: string) =>
      apiFetch<{ emp_code: string; url: string }>('/hr/reveal/national-id-image', { method: 'POST', body: { emp_code } }),
  });
}
export function useRevealPayBand() {
  return useMutation({
    mutationFn: (bandId: string) =>
      apiFetch<{ id: string; min_salary: number; mid_salary: number; max_salary: number }>(
        `/hr/comp/bands/${encodeURIComponent(bandId)}/reveal`, { method: 'POST' },
      ),
  });
}

// === Payroll Runs (for a given period) ===
export function usePayrollRuns(period: string | null) {
  return useQuery({
    queryKey: ['payroll-runs', period],
    queryFn: () => apiFetch(`/owner/payroll/run?period=${period}`),
    enabled: !!period,
  });
}

// === Phase 1C — Commission ===

export interface CommissionTierRow {
  package_id: string;
  tier_order: number;
  min_amount: number;
  max_amount: number | null;
  rate_percent: number | null;
  rate_fixed: number | null;
  notes: string | null;
}

export interface CommissionPackageRow {
  id: string;
  name: string;
  description: string | null;
  basis: 'gross_sales' | 'units_sold' | 'gross_profit';
  min_threshold: number;
  cap: number | null;
  floor: number | null;
  active: boolean;
  tiers: CommissionTierRow[];
}

export function useCommissionPackages() {
  return useQuery({
    queryKey: ['commission', 'packages'],
    queryFn: () => apiFetch<CommissionPackageRow[]>('/hr/commission/packages'),
  });
}
export function useUpsertCommissionPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CommissionPackageRow> & { tiers: CommissionTierRow[] }) =>
      apiFetch<{ id: string }>('/hr/commission/packages', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'packages'] }),
  });
}
export function useDeleteCommissionPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/commission/packages/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'packages'] }),
  });
}

export interface CommissionEligibilityRow {
  id: string;
  emp_code: string;
  package_id: string;
  start_date: string;
  end_date: string | null;
  override_cap: number | null;
  override_floor: number | null;
  active: boolean;
}
export function useCommissionEligibility(empCode: string | null) {
  return useQuery({
    queryKey: ['commission', 'elig', empCode],
    queryFn: () =>
      apiFetch<CommissionEligibilityRow[]>(
        `/hr/commission/eligibility${empCode ? `?emp=${encodeURIComponent(empCode)}` : ''}`,
      ),
  });
}
export function useUpsertCommissionEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CommissionEligibilityRow>) =>
      apiFetch<CommissionEligibilityRow>('/hr/commission/eligibility', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'elig'] }),
  });
}
export function useDeleteCommissionEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/commission/eligibility/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'elig'] }),
  });
}

export interface CommissionRunRow {
  id: string;
  period_start: string;
  period_end: string;
  status: 'entry' | 'calculated' | 'reviewed' | 'locked' | 'voided';
  feeds_payroll_period: string | null;
  locked_at: string | null;
}
export interface CommissionPerformanceRow {
  id: string;
  emp_code: string;
  gross_sales: number;
  units_sold: number;
  gross_profit: number;
  notes: string | null;
  package_id: string | null;
  calculated_amount: number | null;
  calculation_breakdown: unknown | null;
  calculated_at: string | null;
}

export function useCommissionRuns() {
  return useQuery({
    queryKey: ['commission', 'runs'],
    queryFn: () => apiFetch<CommissionRunRow[]>('/hr/commission/runs'),
  });
}
export function useCreateCommissionRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period_start: string; period_end: string }) =>
      apiFetch<{ id: string }>('/hr/commission/runs', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'runs'] }),
  });
}
export function useCommissionPerformance(runId: string | null) {
  return useQuery({
    queryKey: ['commission', 'perf', runId],
    queryFn: () =>
      apiFetch<CommissionPerformanceRow[]>(
        `/hr/commission/runs/${encodeURIComponent(runId!)}/performance`,
      ),
    enabled: !!runId,
  });
}
export function useUpsertCommissionPerformance(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { emp_code: string; gross_sales?: number; units_sold?: number; gross_profit?: number; notes?: string }) =>
      apiFetch(`/hr/commission/runs/${encodeURIComponent(runId)}/performance`, { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission', 'perf', runId] });
      qc.invalidateQueries({ queryKey: ['commission', 'runs'] });
    },
  });
}
export function useCalculateCommission(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ calculated: number }>(`/hr/commission/runs/${encodeURIComponent(runId)}/calculate`, { method: 'POST', body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission', 'perf', runId] });
      qc.invalidateQueries({ queryKey: ['commission', 'runs'] });
    },
  });
}
export function useReviewCommission(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/hr/commission/runs/${encodeURIComponent(runId)}/review`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'runs'] }),
  });
}
export function useLockCommission(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { feeds_payroll_period: string }) =>
      apiFetch<{ fed_entries: number }>(`/hr/commission/runs/${encodeURIComponent(runId)}/lock`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission'] }),
  });
}

// === Phase 2A — Year-end ===

export interface YearEndEmployeeRow {
  emp_code: string;
  emp_name: string;
  months_locked: number;
  gross_ytd: number;
  taxable_ytd: number;
  net_ytd: number;
  tax_ytd: number;
  sso_ytd: number;
  pf_ytd: number;
  ot_pay_ytd: number;
  last_updated: string;
}
export interface YearEndSummary {
  year: number;
  employees: YearEndEmployeeRow[];
  totals: { gross_sum: number; taxable_sum: number; tax_sum: number; sso_sum: number; pf_sum: number };
}
export function useYearEndSummary(year: number) {
  return useQuery({
    queryKey: ['year-end', year],
    queryFn: () => apiFetch<YearEndSummary>(`/owner/year-end?year=${year}`),
  });
}

export interface FiftyTawiData {
  year: number;
  no_data: boolean;
  employee: {
    emp_code: string;
    first_name: string;
    last_name: string;
    nickname?: string | null;
    national_id?: string | null;
    address_current?: string | null;
    sso_number?: string | null;
    position?: string | null;
    department?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  };
  tenant?: { company_name?: string; slug?: string } | null;
  totals?: {
    gross_ytd: number;
    taxable_ytd: number;
    net_ytd: number;
    tax_ytd: number;
    sso_ytd: number;
    pf_ytd: number;
    ot_pay_ytd: number;
    last_updated: string;
  };
}
export function useFiftyTawi(year: number, empCode: string | null) {
  return useQuery({
    queryKey: ['50tawi', year, empCode],
    queryFn: () => apiFetch<FiftyTawiData>(`/owner/year-end/50tawi?year=${year}&emp=${encodeURIComponent(empCode!)}`),
    enabled: !!empCode,
  });
}
export function useRecomputeYtd() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) =>
      apiFetch('/owner/year-end/recompute-ytd', { method: 'POST', body: { year } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['year-end'] }),
  });
}

// === Phase 2B — Termination ===
export interface TerminationPreview {
  emp_code: string;
  emp_name: string;
  effective_date: string;
  reason_code: 'resigned' | 'terminated' | 'retired' | 'contract_end';
  tenure_days: number;
  tenure_years: number;
  daily_rate: number;
  severance_days: number;
  severance_amount: number;
  accrued_leave_days: number;
  accrued_leave_amount: number;
  notice_pay: number;
  total_payout: number;
}

export function usePreviewTermination() {
  return useMutation({
    mutationFn: (body: {
      emp_code: string;
      effective_date: string;
      reason_code: string;
      notice_shortfall_days?: number;
      accrued_leave_days_override?: number;
    }) => apiFetch<TerminationPreview>('/hr/termination/preview', { method: 'POST', body }),
  });
}
export function useCommitTermination() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      emp_code: string;
      effective_date: string;
      reason_code: string;
      notice_shortfall_days?: number;
      accrued_leave_days_override?: number;
      reason_note?: string;
      feeds_payroll_period: string;
    }) => apiFetch('/hr/termination/commit', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'employees'] }),
  });
}

// === หนังสือรับรอง (รับรองเงินเดือน / รับรองการทำงาน) ===
export type CertKind = 'employment' | 'salary';
export interface CertificatePreview {
  kind: CertKind;
  refNo: string;
  issueDateTh: string;
  empName: string;
  empCode: string;
  position?: string | null;
  department?: string | null;
  startDateTh: string;
  endDateTh?: string | null;
  stillEmployed: boolean;
  salaryAmount?: number | null;
  salaryText?: string | null;
  purpose?: string | null;
}
export function useCertificatePreview() {
  return useMutation({
    mutationFn: (q: { emp_code: string; kind: CertKind; purpose?: string }) => {
      const p = new URLSearchParams({ emp: q.emp_code, kind: q.kind });
      if (q.purpose) p.set('purpose', q.purpose);
      return apiFetch<{ preview: CertificatePreview }>(`/hr/certificates/preview?${p.toString()}`);
    },
  });
}
export function useIssueCertificate() {
  return useMutation({
    mutationFn: (body: { emp_code: string; kind: CertKind; purpose?: string }) =>
      apiFetch<{ pdf_url: string; ref_no: string }>('/hr/certificates/issue', { method: 'POST', body }),
  });
}

// === Phase 3 — Shift master ===
export interface ShiftMasterRow {
  id: string;
  code: string;
  name: string;
  start_time: string;
  end_time: string;
  crosses_midnight: boolean;
  break_minutes: number;
  paid_hours: number;
  night_premium_type: 'none' | 'percent' | 'flat';
  night_premium_value: number;
  night_window_start: string;
  night_window_end: string;
  default_weekdays: number[];
  active: boolean;
  flexible?: boolean;
  require_clock_out?: boolean;
  flex_check_in_by?: string | null;
}
export function useShiftMaster() {
  return useQuery({ queryKey: ['shift', 'master'], queryFn: () => apiFetch<ShiftMasterRow[]>('/hr/shift/master') });
}
export function useUpsertShiftMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ShiftMasterRow>) => apiFetch<ShiftMasterRow>('/hr/shift/master', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'master'] }),
  });
}
export function useDeleteShiftMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/hr/shift/master/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'master'] }),
  });
}

export interface ShiftAssignmentRow {
  id: string;
  emp_code: string;
  shift_id: string;
  weekdays: number[];
  start_date: string;
  end_date: string | null;
  active: boolean;
}
export function useShiftAssignments(empCode: string | null) {
  return useQuery({
    queryKey: ['shift', 'assign', empCode],
    queryFn: () => apiFetch<ShiftAssignmentRow[]>(`/hr/shift/assignment${empCode ? `?emp=${encodeURIComponent(empCode)}` : ''}`),
  });
}
export function useShiftAssignmentsByShift(shiftId: string | null) {
  return useQuery({
    queryKey: ['shift', 'assign-by-shift', shiftId],
    queryFn: () => apiFetch<ShiftAssignmentRow[]>(`/hr/shift/assignment?shift=${encodeURIComponent(shiftId!)}`),
    enabled: !!shiftId,
  });
}
export function useUpsertShiftAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ShiftAssignmentRow>) => apiFetch<ShiftAssignmentRow>('/hr/shift/assignment', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}
export function useDeleteShiftAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/hr/shift/assignment/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}

// === วันหยุดหมุนเวียน / นำเข้าตารางเวร (P2/P3) ===
export interface RosterImportRow {
  key: string;
  matched: boolean;
  emp_code: string | null;
  name: string | null;
  ambiguous: boolean;
  days: Array<{ date: string; raw: string; kind: 'shift' | 'off' | 'ignore' | 'unknown'; label?: string }>;
}
export interface RosterImportDiagnostics {
  employees_matched: number;
  unmatched_keys: string[];
  unknown_codes: Array<{ raw: string; count: number }>;
  week_warnings: Array<{ emp_code: string; name: string }>;
  work_cells: number;
  off_cells: number;
  unknown_cells: number;
  writable_entries: number;
  /** จำนวนพนักงานที่ถูกตั้งโหมดเป็น "ตารางเวร" อัตโนมัติตอนนำเข้า (มีเฉพาะตอน commit) */
  roster_mode_set?: number;
  leave_conflicts?: Array<{ emp_code: string; date: string }>;
  /** ช่องที่วันที่อยู่นอกช่วงที่เลือก — ไม่ถูกนำเข้า (รายงานไว้ ไม่ตัดเงียบ) */
  out_of_range_cells?: number;
  /** ช่องที่ระบุมาเป็น "เลขวัน" แต่ช่วงยาวจนเลขนั้นซ้ำ → บอกไม่ได้ว่าเดือนไหน */
  ambiguous_day_cells?: number;
}
export interface RosterImportResult {
  from: string;
  to: string;
  rows: RosterImportRow[];
  entries: Array<{ emp_code: string; date: string; shift_id: string | null }>;
  diagnostics: RosterImportDiagnostics;
}
export interface RosterImportPayload {
  /** ช่วงวันที่ปลายทาง (ข้ามเดือนได้) — คีย์ของ cells เป็น YYYY-MM-DD */
  from: string;
  to: string;
  rows: Array<{ key: string; cells: Record<string, string> }>;
}

export function useRosterImportPreview() {
  return useMutation({
    mutationFn: (body: RosterImportPayload) =>
      apiFetch<RosterImportResult>('/hr/shift/roster/import/preview', { method: 'POST', body }),
  });
}
export function useRosterImportCommit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RosterImportPayload) =>
      apiFetch<{ written: number; diagnostics: RosterImportDiagnostics }>('/hr/shift/roster/import/commit', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}
export function useCodeAlias() {
  return useQuery({
    queryKey: ['shift', 'code-alias'],
    queryFn: () => apiFetch<{ aliases: Record<string, string> }>('/hr/shift/code-alias'),
  });
}
export function useSaveCodeAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aliases: Record<string, string>) =>
      apiFetch<{ aliases: Record<string, string> }>('/hr/shift/code-alias', { method: 'POST', body: { aliases } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'code-alias'] }),
  });
}
export function useCopyPrevMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { from: string; to: string; overwrite: boolean }) =>
      apiFetch<{ from: string; to: string; target_from: string; target_to: string; rows: number }>('/hr/shift/roster/copy-prev-month', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}

export interface WorkScheduleSettings {
  weekend_days: number[];
  workdays_per_week: number;
  hours_per_workday: number;
  holiday_as_workday?: boolean;
}
export function useWorkSchedule() {
  return useQuery({
    queryKey: ['hr', 'work-schedule'],
    queryFn: () => apiFetch<WorkScheduleSettings>('/hr/work-schedule'),
  });
}

export interface RosterRow {
  id: string;
  emp_code: string;
  date: string;
  shift_id: string | null;
  source: 'manual' | 'swap' | 'assignment';
  note: string | null;
}
export function useRoster(from: string, to: string) {
  return useQuery({
    queryKey: ['shift', 'roster', from, to],
    queryFn: () => apiFetch<RosterRow[]>(`/hr/shift/roster?from=${from}&to=${to}`),
    enabled: !!from && !!to,
  });
}

// === ปฏิทินเวร (อ่านอย่างเดียว) — คำขอเดียวได้ครบทั้งตาราง ===
// พนักงานทั่วไปเรียกได้ (เห็นแผนกตัวเอง) · หัวหน้างานขึ้นไปเห็นทุกคนทุกแผนก
export interface RosterViewEmployee {
  emp_code: string;
  name: string;
  nickname: string | null;
  department: string | null;
  position: string | null;
}
export interface RosterViewData {
  from: string;
  to: string;
  scope: 'all' | 'department';
  scope_label: string;
  can_see_leave_type: boolean;
  employees: RosterViewEmployee[];
  roster: Array<{ emp_code: string; date: string; shift_id: string | null }>;
  leaves: Array<{ emp_code: string; date: string; leave_type?: string }>;
  shifts: Array<{ id: string; code: string; name: string; start_time: string; end_time: string }>;
  holidays: Array<{ date: string; name: string }>;
  weekend_days: number[];
  holiday_as_workday: boolean;
}
/** เพดานช่วงวันที่ — ต้องตรงกับ MAX_RANGE_DAYS ฝั่ง worker */
export const ROSTER_VIEW_MAX_DAYS = 92;
export function useRosterView(from: string, to: string) {
  return useQuery({
    queryKey: ['shift', 'roster-view', from, to],
    queryFn: () => apiFetch<RosterViewData>(`/shift/roster-view?from=${from}&to=${to}`),
    enabled: !!from && !!to,
  });
}
export interface ApprovedLeaveLite { emp_code: string; date: string; leave_type: string }
/** วันลาที่อนุมัติแล้วในช่วง (scope ลูกทีม) — ใช้โชว์ป้ายเตือนในหน้าจัดกะ. */
export function useApprovedLeaves(from: string, to: string) {
  return useQuery({
    queryKey: ['leaves', 'approved', from, to],
    queryFn: () => apiFetch<ApprovedLeaveLite[]>(`/hr/leaves/approved?from=${from}&to=${to}`),
    enabled: !!from && !!to,
  });
}
export function useUpsertRoster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { emp_code: string; date: string; shift_id: string | null; note?: string }) =>
      apiFetch('/hr/shift/roster', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'roster'] }),
  });
}

export interface SwapRow {
  id: string;
  date: string;
  requester_emp_code: string;
  requester_shift_id: string | null;
  target_emp_code: string;
  target_shift_id: string | null;
  reason: string | null;
  status: 'awaiting_target' | 'pending' | 'approved' | 'rejected' | 'declined' | 'cancelled';
  target_responded_at?: string | null;
  decided_at: string | null;
  decision_note: string | null;
}
export function useSwapRequests(status?: string) {
  return useQuery({
    queryKey: ['shift', 'swap', status ?? 'all'],
    queryFn: () => apiFetch<SwapRow[]>(`/shift/swap${status ? `?status=${status}` : ''}`),
    refetchInterval: 30_000,
  });
}
export function useCreateSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; requester_emp_code: string; target_emp_code: string; reason?: string }) =>
      apiFetch('/shift/swap', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'swap'] }),
  });
}
export function useDecideSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected'; decision_note?: string }) =>
      apiFetch(`/shift/swap/${encodeURIComponent(args.id)}/decide`, {
        method: 'POST',
        body: { decision: args.decision, decision_note: args.decision_note },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'swap'] }),
  });
}
// สลับเวร — มุมพนักงาน (ดูคำขอที่เกี่ยวกับฉัน + ตอบรับ + เลือกเพื่อน)
export function useMySwaps() {
  return useQuery({
    queryKey: ['shift', 'swap', 'mine'],
    queryFn: () => apiFetch<SwapRow[]>('/shift/swap/mine'),
    refetchInterval: 30_000,
  });
}
export function useRespondSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; accept: boolean }) =>
      apiFetch(`/shift/swap/${encodeURIComponent(args.id)}/respond`, { method: 'POST', body: { accept: args.accept } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'swap'] }),
  });
}
export interface CoworkerRow { emp_code: string; name: string; nickname: string | null; position: string | null }
export function useCoworkers() {
  return useQuery({ queryKey: ['me', 'coworkers'], queryFn: () => apiFetch<CoworkerRow[]>('/me/coworkers') });
}

// === ตารางกะของตัวเอง (พนักงานทุกคนดูได้ — รายวัน/สัปดาห์/เดือน) ===
export interface MyShiftDay {
  date: string;
  shift: { code: string; name: string; start_time: string; end_time: string; flexible: boolean } | null;
  day_off: boolean;
  holiday: string | null;
}
export interface MyShiftsResponse {
  days: MyShiftDay[];
  schedule_mode: string;
  attendance_exempt: boolean;
}
export function useMyShifts(from: string, to: string) {
  return useQuery({
    queryKey: ['me', 'shifts', from, to],
    queryFn: () => apiFetch<MyShiftsResponse>(`/me/shifts?from=${from}&to=${to}`),
    enabled: !!from && !!to,
  });
}

// === จ่ายนอกรอบเงินเดือน (ค่าคอม/OT จ่ายแยก — บันทึกว่าจ่ายแล้ว) ===
export interface PayoutType {
  id: string;
  code: string;
  label: string;
  kind: 'earning' | 'deduction';
  sort_order: number;
  active: boolean;
}
export interface PayoutRoundTotals { people: number; gross: number; deduct: number; wht: number; net: number }
export interface PayoutRoundSummary {
  id: string;
  title: string;
  pay_date: string;
  status: 'draft' | 'paid' | 'cancelled';
  wht_default_percent: number;
  note: string | null;
  paid_at: string | null;
  created_at: string;
  period?: string | null;
  round_kind?: 'salary' | 'commission' | 'other' | null;
  slip_released?: boolean;
  // ⭐ M3: ให้ UI เตือน "เคย export ไฟล์โอนแล้วเมื่อ X" ก่อนโหลดซ้ำ (round detail
  // เท่านั้นที่ส่งมา — listPayoutRoundsHandler ไม่ได้ select คอลัมน์นี้)
  bank_transfer_exported_at?: string | null;
  bank_transfer_export_count?: number;
  totals: PayoutRoundTotals;
}
export interface PayoutItemRow {
  id: string;
  emp_code: string;
  breakdown: Record<string, number>;
  gross: number;
  deduct_total: number;
  wht_percent: number;
  wht_amount: number;
  net: number;
  note: string | null;
  employee: { first_name: string; last_name: string; nickname: string | null; department: string | null; position: string | null } | null;
}
export interface PayoutRoundDetail {
  round: Omit<PayoutRoundSummary, 'totals'>;
  items: PayoutItemRow[];
}

export function usePayoutTypes() {
  return useQuery({ queryKey: ['payouts', 'types'], queryFn: () => apiFetch<PayoutType[]>('/hr/payouts/types') });
}
export function useUpsertPayoutType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id?: string; code: string; label: string; kind?: 'earning' | 'deduction'; sort_order?: number; active?: boolean }) =>
      apiFetch<PayoutType>('/hr/payouts/types', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payouts', 'types'] }),
  });
}
export function usePayoutRounds(year: number) {
  return useQuery({
    queryKey: ['payouts', 'rounds', year],
    queryFn: () => apiFetch<PayoutRoundSummary[]>(`/hr/payouts/rounds?year=${year}`),
  });
}
export function useUpsertPayoutRound() {
  const qc = useQueryClient();
  return useMutation({
    // period = งวด (YYYY-MM) เดือนที่คิดเงิน — ไม่ส่งมาหลังบ้านจะเดาจากชื่อรอบ/วันโอน
    mutationFn: (body: { id?: string; title: string; pay_date: string; wht_default_percent?: number; note?: string; period?: string }) =>
      apiFetch<{ id: string; status: string }>('/hr/payouts/rounds', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payouts'] }),
  });
}
export function usePayoutRound(id: string | null) {
  return useQuery({
    queryKey: ['payouts', 'round', id],
    queryFn: () => apiFetch<PayoutRoundDetail>(`/hr/payouts/rounds/${id}`),
    enabled: !!id,
  });
}
export interface PayoutComputedRow {
  emp_code: string;
  holiday: number;
  diligence: number;
  /** ทำไมไม่ได้เบี้ยขยัน — optional เผื่อ LIFF ขึ้นก่อน worker */
  diligence_reasons?: string[];
  late: number;
  /** ยอดหักลืมสแกนหน้า — optional เผื่อ LIFF ขึ้นก่อน worker */
  forget_in?: number;
  forget_out?: number;
  forget_in_count?: number;
  forget_out_count?: number;
}
export interface PayoutComputed {
  period: string | null;
  window_start?: string;
  window_end?: string;
  diligence_rule?: 'old' | 'new' | 'unavailable';
  forget_rule?: 'old' | 'new' | 'unavailable';
  rows: PayoutComputedRow[];
  warning?: string;
}
/** ตัวเลขที่ระบบคิดให้ต่อคนของรอบนี้ — ใช้เทียบกับยอดที่บันทึกในรอบ */
export function usePayoutRoundComputed(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['payouts', 'round', id, 'computed'],
    queryFn: () => apiFetch<PayoutComputed>(`/hr/payouts/rounds/${id}/computed`),
    enabled: !!id && enabled,
  });
}
export function useSavePayoutItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      round_id: string;
      items: Array<{ emp_code: string; breakdown: Record<string, number>; wht_percent?: number; wht_amount?: number; note?: string }>;
      remove_emp_codes?: string[];
    }) =>
      apiFetch<{ saved: number; removed: number }>(`/hr/payouts/rounds/${args.round_id}/items`, {
        method: 'POST',
        body: { items: args.items, remove_emp_codes: args.remove_emp_codes },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payouts'] }),
  });
}
export function useMarkPayoutPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; status: string }>(`/hr/payouts/rounds/${id}/mark-paid`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payouts'] }),
  });
}
export function useCancelPayoutRound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; status: string }>(`/hr/payouts/rounds/${id}/cancel`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payouts'] }),
  });
}
export interface PayoutReportRow {
  emp_code: string;
  rounds: number;
  by_type: Record<string, number>;
  gross: number;
  deduct: number;
  wht: number;
  net: number;
  employee: { first_name: string; last_name: string; nickname: string | null; department: string | null; position: string | null } | null;
}
export function usePayoutReport(year: number) {
  return useQuery({
    queryKey: ['payouts', 'report', year],
    queryFn: () => apiFetch<{ year: string; employees: PayoutReportRow[] }>(`/hr/payouts/report?year=${year}`),
  });
}
export interface MyPayoutRow {
  round_id: string;
  title: string;
  pay_date: string;
  breakdown: Record<string, number>;
  gross: number;
  deduct_total: number;
  wht_percent: number;
  wht_amount: number;
  net: number;
}
export function useMyPayouts() {
  return useQuery({
    queryKey: ['me', 'payouts'],
    queryFn: () => apiFetch<{ types: Array<{ code: string; label: string; kind?: 'earning' | 'deduction' }>; payouts: MyPayoutRow[] }>('/me/payouts'),
  });
}

// สลิปงวด — เหลือเฉพาะรอบเงินเดือน (2026-07-30 แยกขาดจากค่าคอม/OT), preview จนกว่าค่าคอมจ่าย/HR ปลดล็อก
// ค่าคอม/OT ของงวดโหลดแยกเป็น "สลิปรายรอบ" ต่อ round_id ผ่าน /me/payouts/:round_id/slip-pdf
export interface PayslipInstallment { round_id: string; kind: string; title: string; pay_date: string; status: string; net: number }
export interface PayslipPeriod {
  period: string; period_label: string;
  net: number;         // รวมทุกรอบที่จ่ายแล้วของงวด (เงินเดือน+ค่าคอม/OT ปนกัน)
  salary_net: number;  // เฉพาะรอบเงินเดือน — ตรงกับยอดในสลิปเงินเดือน PDF เป๊ะ (M3 fix)
  releasable: boolean; commission_paid: boolean; has_salary_slip: boolean; installments: PayslipInstallment[];
}
export function useMyPayslips() {
  return useQuery({
    queryKey: ['me', 'payslips'],
    queryFn: () => apiFetch<{ payslips: PayslipPeriod[] }>('/me/payslips'),
  });
}

// Dashboard: Top 5 มาสาย / ลางาน ของงวด
export interface AttendanceTopRow { emp_code: string; name: string; late_days?: number; late_minutes?: number; leave_days?: number; absent_days?: number }
export interface AttendanceTopData { period: string; periods: string[]; top_late: AttendanceTopRow[]; top_leave: AttendanceTopRow[] }
export function useAttendanceTop(period?: string) {
  return useQuery({
    queryKey: ['attendance-top', period ?? 'latest'],
    queryFn: () => apiFetch<AttendanceTopData>(`/owner/stats/attendance-top${period ? `?period=${encodeURIComponent(period)}` : ''}`),
  });
}

// HR อนุมัติออกสลิปงวด (ปลดล็อกแม้ไม่มีค่าคอม)
export function useReleasePayslipPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => apiFetch(`/hr/payslips/${period}/release`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payouts'] }),
  });
}

// === ขอย้ายวันหยุดตัวเอง (สลับวัน) ===
export interface ShiftChangeRow {
  id: string;
  /** 'day_off_move' = ย้ายวันหยุด (สลับ 2 วัน) · 'shift_change' = เปลี่ยนกะวันเดียว */
  request_type?: 'day_off_move' | 'shift_change';
  original_date: string;
  target_date: string;
  original_shift_id: string | null;
  target_shift_id: string | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  is_urgent?: boolean;
  submitted_at: string;
  decided_at: string | null;
  rejection_reason: string | null;
}
export interface ShiftOption { id: string; code: string; name: string; start_time: string; end_time: string }
/** กะที่ใช้งานอยู่ (พนักงานทุกคนเรียกได้) — ใช้ในฟอร์มขอย้ายวันหยุดให้เลือกกะวันกลับมาทำงาน. */
export function useShiftOptions() {
  return useQuery({
    queryKey: ['me', 'shift-options'],
    queryFn: () => apiFetch<ShiftOption[]>('/me/shift-options'),
    staleTime: 5 * 60_000,
  });
}
export function useMyShiftChanges() {
  return useQuery({
    queryKey: ['me', 'shift-change'],
    queryFn: () => apiFetch<ShiftChangeRow[]>('/me/shift-change-requests'),
    refetchInterval: 30_000,
  });
}
export function useCreateShiftChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { original_date: string; target_date: string; original_shift_id?: string; reason?: string }) =>
      apiFetch('/me/shift-change-request', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'shift-change'] }),
  });
}
export function useDecideShiftChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected'; rejection_reason?: string }) =>
      apiFetch(`/shift-change/${encodeURIComponent(args.id)}/decide`, {
        method: 'POST',
        body: { decision: args.decision, rejection_reason: args.rejection_reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}
/** ขอเปลี่ยนกะการทำงานวันเดียว (เช่น เข้า 08:00 → เข้า 12:00) — ใช้คิว/รายการเดียวกับย้ายวันหยุด. */
export function useCreateChangeShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; target_shift_id: string; reason?: string }) =>
      apiFetch('/me/change-shift-request', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'shift-change'] }),
  });
}

// === รับรองเวลาเข้างาน ===
export interface AttendanceCorrectionRow {
  id: string;
  emp_code?: string;
  emp_name?: string;
  date: string;
  kind: 'morning_in' | 'evening_out';
  requested_time: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  submitted_at?: string;
  decided_at?: string | null;
  rejection_reason?: string | null;
  /** เวลาสแกนปัจจุบันของรอบที่ขอ (HH:mm) — คำขอจะเขียนทับค่านี้ */
  current_scan_time?: string | null;
  /** เวลาปัจจุบันเป็นสแกนจริงของพนักงาน (ไม่ใช่ที่ HR ลงเอง) */
  current_is_real?: boolean;
}
export function useMyAttendanceCorrections() {
  return useQuery({
    queryKey: ['me', 'attendance-correction'],
    queryFn: () => apiFetch<AttendanceCorrectionRow[]>('/me/attendance-corrections'),
    refetchInterval: 30_000,
  });
}
export function useCreateAttendanceCorrection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; kind: 'morning_in' | 'evening_out'; requested_time: string; reason: string }) =>
      apiFetch('/me/attendance-correction', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'attendance-correction'] }),
  });
}
export function usePendingAttendanceCorrections(enabled = true) {
  return useQuery({
    queryKey: ['hr', 'attendance-correction', 'pending'],
    queryFn: () => apiFetch<AttendanceCorrectionRow[]>('/hr/attendance-corrections'),
    enabled,
    refetchInterval: 30_000,
  });
}
export function useDecideAttendanceCorrection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected'; rejection_reason?: string; waive_deduction?: boolean; evidence_path?: string }) =>
      apiFetch<{ id: string; status: string; label: string; deduction_waived: boolean }>(`/attendance-correction/${encodeURIComponent(args.id)}/decide`, {
        method: 'POST',
        body: {
          decision: args.decision,
          rejection_reason: args.rejection_reason,
          waive_deduction: args.waive_deduction,
          evidence_path: args.evidence_path,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'attendance-correction'] });
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['employee-attendance'] });
    },
  });
}

// === แพทเทิร์นหมุนเวรอัตโนมัติ ===
export interface RotationPatternRow {
  id: string;
  code: string;
  name: string;
  sequence: (string | null)[];
  cycle_length: number;
  active: boolean;
}
export function useRotationPatterns() {
  return useQuery({ queryKey: ['shift', 'rotation'], queryFn: () => apiFetch<RotationPatternRow[]>('/hr/shift/rotation') });
}
export function useUpsertRotationPattern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id?: string; code: string; name: string; sequence: (string | null)[]; active: boolean }) =>
      apiFetch<RotationPatternRow>('/hr/shift/rotation', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'rotation'] }),
  });
}
export function useDeleteRotationPattern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/hr/shift/rotation/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}
export function useGenerateRotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) =>
      apiFetch<{ period: string; written: number; employees: number; skipped_manual: number }>('/hr/shift/rotation/generate', { method: 'POST', body: { period } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}

// === Tickets (ร้องเรียน / แจ้งเหตุ / ร้องทุกข์ + คำขอแก้ย้อนหลัง) ===
export type TicketCategory =
  | 'complaint' | 'report' | 'grievance'
  | 'leave_backdate' | 'leave_type_change' | 'leave_edit_locked' | 'schedule_change';
export type TicketStatus =
  | 'open' | 'under_review' | 'escalated' | 'approved' | 'rejected' | 'resolved';

export interface Ticket {
  id: string;
  emp_code: string;
  category: TicketCategory;
  subject: string;
  detail: string;
  ref_note: string | null;
  status: TicketStatus;
  hr_note: string | null;
  hr_at: string | null;
  decision_note: string | null;
  decided_role: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useTickets(opts?: { status?: string; mine?: boolean }) {
  const qs = new URLSearchParams();
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.mine) qs.set('mine', '1');
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['tickets', opts?.status ?? 'all', opts?.mine ? 'mine' : 'all'],
    queryFn: () => apiFetch<Ticket[]>(`/tickets${suffix}`),
  });
}
export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { category: TicketCategory; subject: string; detail: string; ref_note?: string }) =>
      apiFetch('/tickets', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}
export function useTriageTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: string; action: 'review' | 'escalate' | 'resolve'; hr_note?: string }) =>
      apiFetch('/tickets/triage', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}
export function useDecideTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: string; decision: 'approved' | 'rejected'; decision_note: string }) =>
      apiFetch('/tickets/decide', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}

// === เรื่อง 3 — self-confirm clock-out ===
export interface MissingClockOut { date: string; detail: string | null; status: string }
export function useMissingClockOut() {
  return useQuery({
    queryKey: ['missing-clockout'],
    queryFn: () => apiFetch<MissingClockOut[]>('/checkin/missing-clockout'),
  });
}
export function useConfirmClockOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; mode: 'shift_end' | 'actual'; actual_time?: string; reason?: string }) =>
      apiFetch('/checkin/confirm-clockout', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['missing-clockout'] });
      qc.invalidateQueries({ queryKey: ['checkin-status'] });
    },
  });
}

// === เรื่อง 1 — emergency leave HR review ===
export interface EmergencyPending {
  leave_id: string;
  emp_code: string;
  date: string;
  reason: string | null;
  conditional_evidence_due: string | null;
  created_at: string;
}
export function useEmergencyPending() {
  return useQuery({
    queryKey: ['emergency-pending'],
    queryFn: () => apiFetch<EmergencyPending[]>('/leave/emergency/pending'),
    refetchInterval: 30_000,
  });
}
export function useDecideEmergencyLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { leave_id: string; decision: 'paid' | 'unpaid' | 'absent'; reason: string; deduct_quota: boolean }) =>
      apiFetch('/leave/emergency/decide', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emergency-pending'] }),
  });
}

// === Sidebar badge counts (pending items per menu item) ===
// Single hook so AdminLayout can show "(N)" next to each approval menu without
// every page bolting on its own fetcher. HR-only endpoints are role-gated so
// SUPERVISOR (sees /approvals but not HR routes) doesn't trigger 403s.
export type MenuBadgeKey = 'approvals' | 'emergency' | 'pendingChanges' | 'tickets' | 'approvalsTotal';

export function useMenuBadgeCounts(role: string): Record<MenuBadgeKey, number> {
  const isAdmin = role === 'OWNER' || role === 'HR' || role === 'MANAGER';
  const isSupervisorPlus = isAdmin || role === 'SUPERVISOR';

  const approvals = useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: () => apiFetch<unknown[]>('/approvals/pending'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: isSupervisorPlus,
  });
  const emergency = useQuery({
    queryKey: ['emergency-pending'],
    queryFn: () => apiFetch<unknown[]>('/leave/emergency/pending'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: isAdmin,
  });
  const pendingChanges = useQuery({
    queryKey: ['pending-changes'],
    queryFn: () => apiFetch<Array<{ status: string }>>('/hr/pending-changes'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: isAdmin,
  });
  const tickets = useQuery({
    queryKey: ['tickets', 'all', 'all'],
    queryFn: () => apiFetch<Array<{ status: string }>>('/tickets'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: isAdmin,
  });

  const approvalsCount = Array.isArray(approvals.data) ? approvals.data.length : 0;
  const emergencyCount = Array.isArray(emergency.data) ? emergency.data.length : 0;
  const pendingChangesCount = Array.isArray(pendingChanges.data)
    ? pendingChanges.data.filter((c) => c.status === 'pending').length
    : 0;
  const ticketsCount = Array.isArray(tickets.data)
    ? tickets.data.filter((t) => t.status === 'open' || t.status === 'under_review' || t.status === 'escalated').length
    : 0;
  return {
    approvals: approvalsCount,
    emergency: emergencyCount,
    pendingChanges: pendingChangesCount,
    tickets: ticketsCount,
    // Sum used by the consolidated "อนุมัติคำขอ" menu entry (single button
    // replaces 4 separate buttons; SUPERVISOR only contributes approvals).
    approvalsTotal: approvalsCount + emergencyCount + pendingChangesCount + ticketsCount,
  };
}

// === Reset tenant data (OWNER only) ===
export interface ResetPreview {
  work: Array<{ label: string; count: number }>;
  setup: Array<{ label: string; count: number }>;
}
export function useResetData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { mode: 'work' | 'all'; confirm: string }) =>
      apiFetch<{ ok: boolean; result: unknown }>('/owner/reset-data', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

// === Workflow config (OWNER/HR) — ผังขั้นตอนสะท้อน config จริง ===
export interface ApproverLevel { label: string; roleKind: 'supervisor' | 'admin' }
export interface WorkflowConfig {
  approvalLevels: number;
  approverLevels: ApproverLevel[];
  usesShifts: boolean;
  usesCommission: boolean;
  usesLeavePackages: boolean;
}
export function useWorkflowConfig() {
  return useQuery({
    queryKey: ['workflow-config'],
    queryFn: () => apiFetch<WorkflowConfig>('/owner/workflow-config'),
    staleTime: 5 * 60_000,
  });
}

// === Payroll Blueprint (OWNER/HR) — config snapshot ทั้งบริษัทในที่เดียว ===
export interface SlipLineItem { label: string; amount: number }
export interface Blueprint {
  generated_at: string;
  company: { name: string; tax_id: string | null; address: string | null; phone: string | null; signer_name: string | null; signer_title: string | null; has_logo: boolean };
  pay_cycle: { cutoff_day: number | null; payday_day: number | null; backdated_mode: string | null; timezone: string };
  work_rules: { workdays_per_week: number; weekend_days: number[]; hours_per_workday: number; late_grace_minutes: number; ot_weekly_cap_hours: number; solo_mode: boolean };
  statutory: {
    year: number;
    sso: { rate_percent: number | null; ceiling: number | null; monthly_cap: number | null };
    personal_allowance: number;
    expense_allowance_note: string;
    min_wage: number | null;
    ot_multipliers: { ot1: number; holiday_work_monthly: number; holiday_work_daily: number; holiday_ot: number };
    tax_brackets_count: number;
  };
  workforce: { total_active: number; monthly: number; daily: number; salary_covered: number; salary_missing: Array<{ emp_code: string; name: string }> };
  allowances: Array<{ name: string; preset: string; taxable: boolean; ssoable: boolean; pfable: boolean; frequency: string; default_amount: number | null; default_rate: number | null; eligible_count: number }>;
  deductions: { statutory: Array<{ name: string; kind: string; default_amount: number | null; eligible_count: number }>; fixed: Array<{ name: string; kind: string; default_amount: number | null; eligible_count: number }>; section76_note: string };
  shifts: Array<{ name: string; start: string; end: string; crosses_midnight: boolean; night_premium_type: string; night_premium_value: number | null }>;
  leave: { packages: Array<{ name: string; sick: number | null; personal: number | null; vacation: number | null; prorate_on_join: boolean; carry_over_max: number | null }>; assigned_count: number };
  approval: { levels: number; by_level: Array<{ level: number; approvers: Array<{ name: string; role: string }> }>; rules: Array<{ kind: string; leave_type: string | null; ot_type: string | null; required_levels: number }>; notify: { employees_with_approver: number; employees_using_fallback: number; fallback_role: 'hr' | 'owner' | 'none'; fallback_recipients: string[] } };
  holidays: { year: number; count: number; items: Array<{ date: string; name: string; type: string }> };
  commission: { uses: boolean; packages: Array<{ name: string; basis: string; tiers: number }> };
  sample_slip: { period: string; line_items: { earnings?: SlipLineItem[]; deductions?: SlipLineItem[]; earnings_total?: number; deductions_total?: number } } | null;
  readiness: { pct: number; complete: boolean; items: Array<{ key: string; label: string; done: boolean; route: string; optional?: boolean }>; next_step: { key: string; label: string; route: string } | null };
}
export function useBlueprint() {
  return useQuery({
    queryKey: ['blueprint'],
    queryFn: () => apiFetch<Blueprint>('/owner/blueprint'),
    staleTime: 2 * 60_000,
  });
}

// ─── Compensation (โครงสร้างค่าตอบแทน) — PayPilot = SoT (ADR 0003) — /hr/comp/* ───
// Ported from eval-pilot; PayPilot now owns all compensation tables.

export type CompStrategy = 'aggressive' | 'fair' | 'lean' | 'custom';
export interface CompBand {
  id: string;
  position_id: string;
  min_salary: number | null;
  mid_salary: number | null;
  max_salary: number | null;
  currency: string;
  strategy: CompStrategy | null;
  effective_date?: string;
  is_archived?: boolean;
  /** เรื่อง 3b — true = ช่วงเงินเดือนถูกมาส์กสำหรับ role นี้ (min/mid/max = null) */
  pay_masked?: boolean;
}
export function useCompBands() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-bands'], queryFn: () => apiFetch<CompBand[]>('/hr/comp/bands') });
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? apiFetch<CompBand>(`/hr/comp/bands/${id}`, { method: 'PATCH', body })
        : apiFetch<CompBand>('/hr/comp/bands', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-bands'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ archived: boolean }>(`/hr/comp/bands/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-bands'] }),
  });
  return { ...list, save, del };
}

// salary_structure cell: min/q1/q2(mid)/q3/max per (department × level).
export interface CompStructureCell {
  id: string;
  department_id: string;
  level: number;
  min_salary: number | null;
  q1_salary: number | null;
  q2_salary: number | null;
  q3_salary: number | null;
  max_salary: number | null;
}
export function useCompStructure() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-structure'], queryFn: () => apiFetch<CompStructureCell[]>('/hr/comp/structure') });
  const upsert = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<CompStructureCell>('/hr/comp/structure', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-structure'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/comp/structure/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-structure'] }),
  });
  // AI-fill writes the grid directly server-side and returns counts.
  const aiFill = useMutation({
    mutationFn: (body: { strategy: string }) =>
      apiFetch<{ upserted: number; errors: unknown[]; total: number }>('/hr/comp/structure/ai-fill', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-structure'] }),
  });
  return { ...list, upsert, del, aiFill };
}

export interface BenefitLevers {
  raise_pct?: number;
  bonus_months?: number;
  reward_bonus?: number;          // เงินรางวัล/โบนัสพิเศษ (บาท, จ่ายครั้งเดียว/ปี)
  health_insurance?: number;      // ประกันสุขภาพ (บาท/ปี)
  life_insurance?: number;        // ประกันชีวิต/อุบัติเหตุ (บาท/ปี)
  provident_fund_pct?: number;
  allowance?: number;             // เบี้ยเลี้ยง (บาท/เดือน)
  travel_allowance?: number;      // ค่าเดินทาง (บาท/เดือน)
  phone_allowance?: number;       // ค่าโทรศัพท์ (บาท/เดือน)
  position_allowance?: number;    // ค่าตำแหน่ง (บาท/เดือน)
  meal_allowance?: number;        // ค่าอาหาร (บาท/เดือน)
  housing_allowance?: number;     // ค่าที่พัก (บาท/เดือน)
  uniform_allowance?: number;     // ค่าชุด/ยูนิฟอร์ม (บาท/ปี)
}
export interface BenefitPackage {
  id: string;
  name: string;
  description: string | null;
  levers: BenefitLevers;
  bonus_basis: string | null;
  status: string;
  assignment_count?: number;
}
export interface BenefitAssignment {
  id: string;
  employee_id: string;
  employee_name?: string;
  computed_cost: number;
  status: string;
}
export function useCompBenefits() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-benefits'], queryFn: () => apiFetch<BenefitPackage[]>('/hr/comp/benefits') });
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? apiFetch<BenefitPackage>(`/hr/comp/benefits/${id}`, { method: 'PATCH', body })
        : apiFetch<BenefitPackage>('/hr/comp/benefits', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-benefits'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ archived: boolean }>(`/hr/comp/benefits/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-benefits'] }),
  });
  const assign = useMutation({
    mutationFn: ({ id, employee_ids }: { id: string; employee_ids: string[] }) =>
      apiFetch<{ assigned: number }>(`/hr/comp/benefits/${id}/assign`, { method: 'POST', body: { employee_ids } }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['comp-benefits'] });
      qc.invalidateQueries({ queryKey: ['comp-benefit', v.id] });
    },
  });
  const unassign = useMutation({
    mutationFn: (aid: string) => apiFetch<{ removed: boolean }>(`/hr/comp/benefits/assignments/${aid}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comp-benefits'] });
      qc.invalidateQueries({ queryKey: ['comp-benefit'] });
    },
  });
  return { ...list, save, del, assign, unassign };
}
export function useBenefitDetail(id: string | null) {
  return useQuery({
    queryKey: ['comp-benefit', id],
    queryFn: () => apiFetch<BenefitPackage & { assignments: BenefitAssignment[] }>(`/hr/comp/benefits/${id}`),
    enabled: !!id,
  });
}

export interface CompComponent {
  type?: string;
  amount?: number;
  frequency?: string;
}
export interface CompPackage {
  id: string;
  position_id: string;
  base_salary: number;
  components: CompComponent[];
  variable_total: number;
  benefit_total: number;
  ctc_total: number;
  notes: string | null;
}
export function useCompPackages() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-packages'], queryFn: () => apiFetch<CompPackage[]>('/hr/comp/packages') });
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? apiFetch<CompPackage>(`/hr/comp/packages/${id}`, { method: 'PATCH', body })
        : apiFetch<CompPackage>('/hr/comp/packages', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-packages'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/comp/packages/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-packages'] }),
  });
  return { ...list, save, del };
}

export interface VariablePay {
  id: string;
  position_id: string | null;
  level: number | null;
  name: string;
  type: string;
  formula: Record<string, unknown>;
  frequency: string | null;
  notes: string | null;
}
export function useCompVariablePay() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-variable-pay'], queryFn: () => apiFetch<VariablePay[]>('/hr/comp/variable-pay') });
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? apiFetch<VariablePay>(`/hr/comp/variable-pay/${id}`, { method: 'PATCH', body })
        : apiFetch<VariablePay>('/hr/comp/variable-pay', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-variable-pay'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/comp/variable-pay/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-variable-pay'] }),
  });
  return { ...list, save, del };
}

// === นโยบาย/ประกาศการทำงาน (Work Policies) ===
export interface WorkPolicy {
  id: string; slug: string; title: string; body: string;
  category: 'time' | 'ot' | 'leave' | 'holiday' | 'general';
  version: number; sort_order: number; source: 'system' | 'custom';
  doc_code: string | null; effective_date: string | null; announced_at: string | null;
  published?: boolean; updated_at: string;
}
export interface PolicyItem extends WorkPolicy { acknowledged: boolean; acked_version: number | null }
export interface AdminPolicy extends WorkPolicy { ack_count: number }
export interface PolicyAck { emp_code: string | null; display_name: string | null; version: number; acknowledged_at: string }

export function usePolicies() {
  return useQuery({ queryKey: ['policies'], queryFn: () => apiFetch<{ items: PolicyItem[] }>('/policies') });
}
export function useAckPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { policy_id: string; version: number }) =>
      apiFetch<{ ok: boolean }>('/policies/ack', { method: 'POST', body: b }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}
export function useAdminPolicies() {
  return useQuery({ queryKey: ['owner', 'policies'], queryFn: () => apiFetch<{ items: AdminPolicy[]; employee_count: number }>('/owner/policies') });
}
export function useUpsertPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: Record<string, unknown>) =>
      apiFetch<AdminPolicy & { notified?: number }>('/owner/policies', { method: 'POST', body: b }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['owner', 'policies'] }); qc.invalidateQueries({ queryKey: ['policies'] }); },
  });
}
export function useDeletePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean }>(`/owner/policies/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner', 'policies'] }),
  });
}
export function useRegeneratePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<AdminPolicy & { notified?: number }>(`/owner/policies/${id}/regenerate`, { method: 'POST', body: {} }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['owner', 'policies'] }); qc.invalidateQueries({ queryKey: ['policies'] }); },
  });
}
export function usePolicyAcks(id: string | null) {
  return useQuery({
    queryKey: ['owner', 'policy-acks', id],
    queryFn: () => apiFetch<{ policy: WorkPolicy; acks: PolicyAck[] }>(`/owner/policies/${id}/acks`),
    enabled: !!id,
  });
}

// === ประวัติการจ่ายเงินเดือน (Payroll history) ===
export interface PayrollHistoryRow {
  period: string; employee_count: number;
  total_gross: number; total_net: number; total_tax: number;
  status: string; computed_at: string | null;
}
export interface PayrollRunRow {
  id: string; emp_code: string; name?: string; gross: number; net: number;
  tax_amount: number; sso_amount: number; deductions_total: number; status: string;
  bank?: string | null; bank_account_masked?: string | null;
  first_name?: string | null; last_name?: string | null; nickname?: string | null;
}
export function usePayrollHistory() {
  return useQuery({ queryKey: ['owner', 'payroll-history'], queryFn: () => apiFetch<{ items: PayrollHistoryRow[] }>('/owner/payroll/history') });
}
export function usePayrollRunRows(period: string | null) {
  return useQuery({
    queryKey: ['owner', 'payroll-run-rows', period],
    // R6 — endpoint คืน { rows, … } แล้ว
    queryFn: () => apiFetch<{ rows: PayrollRunRow[] }>(`/owner/payroll/run?period=${encodeURIComponent(period as string)}`)
      .then((d) => d.rows ?? []),
    enabled: !!period,
  });
}
// usePayrollRunDetail + PayrollRunDetail มีอยู่แล้วด้านบน (ใช้ตัวเดิมร่วมกัน)

// === ตรวจรายการหักตามเวลา (ลาไม่รับค่าจ้าง / ขาดงาน / มาสาย) ===

export interface DockingReviewRow {
  emp_code: string;
  name: string;
  department: string | null;
  employee_type: 'monthly' | 'daily';
  base_salary: number;
  unpaid_leave_days: number;
  absent_days: number;
  late_minutes: number;
  auto: { unpaid_leave: number; absent: number; late: number };
  /** ยอดหักลืมสแกน แสดงอย่างเดียว ตารางคำเคาะไม่มีช่องนี้ — optional เผื่อ LIFF ขึ้นก่อน worker */
  forget?: { in: number; out: number };
  allowed: { unpaid_leave: boolean; absent: boolean; late: boolean };
  skip_reason: string | null;
  /** เคยตรวจไว้ แต่ข้อมูลเวลาเปลี่ยนหลังจากนั้น → ต้องตรวจใหม่ */
  stale: boolean;
  /** ลายนิ้วมือของข้อมูลที่จอกำลังเห็น — ต้องส่งกลับตอน Save */
  src_hash: string;
  /** ชนิดที่ไฟล์นำเข้า "อาจ" หักไว้แล้ว หน่วยสตางค์ — ข้อเสนอแนะ ติ๊กกลับได้ */
  file_deducted_satang: Partial<Record<'unpaid_leave' | 'absent' | 'late', number>>;
  /** รายการหักจากไฟล์ที่เข้าข่ายซ้ำ (บาท) */
  file_labels: Array<{ label: string; amount: number }>;
  /** ฐานค่าจ้างของงวดที่ใช้จริง (เท่ากับที่ตอนรันเงินเดือนคิด) */
  pay_base: number;
  saved: {
    deduct_unpaid: boolean; unpaid_amount: number;
    deduct_absent: boolean; absent_amount: number;
    deduct_late: boolean; late_amount: number;
    note: string | null;
    reviewed_at: string | null;
    /** เลขรุ่นที่จอเห็น — ส่งกลับตอน Save เพื่อกันสองแท็บเขียนทับกัน */
    revision: number | null;
  } | null;
}

export interface DockingReviewResponse {
  period: string;
  locked: boolean;
  needs_reconcile: boolean;
  rows: DockingReviewRow[];
  reviewed_count: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
  /** ยังไม่ได้รัน migration ของตารางนี้ */
  review_table_missing?: boolean;
  /** รหัสพนักงานที่ถอดรหัสฐานเงินเดือนไม่ได้ (ไม่โผล่ในตาราง) */
  decrypt_failed?: string[];
}

export interface DockingReviewSaveRow {
  emp_code: string;
  src_hash: string;
  expected_revision: number | null;
  deduct_unpaid: boolean; unpaid_amount: number;
  deduct_absent: boolean; absent_amount: number;
  deduct_late: boolean; late_amount: number;
  note: string | null;
}

export function useDockingReview(period: string, salaryOnly = false) {
  return useQuery({
    queryKey: ['owner', 'docking-review', period, salaryOnly],
    queryFn: () => apiFetch<DockingReviewResponse>(
      `/owner/payroll/docking-review?period=${period}${salaryOnly ? '&salary_only=true' : ''}`,
    ),
    enabled: !!period,
  });
}

export function useSaveDockingReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; salary_only?: boolean; rows: DockingReviewSaveRow[] }) =>
      apiFetch<{ period: string; saved: number; reviewed_at: string; needs_rerun?: boolean }>('/owner/payroll/docking-review', {
        method: 'PUT',
        body,
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['owner', 'docking-review', vars.period] });
      qc.invalidateQueries({ queryKey: ['payroll-runs'] });
    },
  });
}
